import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { User, Film, Car, CloudUpload, FileText } from 'lucide-react';
import { listSessions, getSessionFacets, getSessionStats, loadSession,
         deleteSession, bulkDeleteSessions, downloadSessionFile, patchSessionMeta,
         uploadCsv, uploadVideo, uploadXrk, uploadMotec, uploadSmartycam3,
         searchUsers, shareSession, shareSessionWithAll,
         listGroups, shareSessionWithGroup,
         listShares, removeShare, removeGlobalShare,
         listGroupShares, removeGroupShare,
         getVtrQueue, createPublicLink, sendSessionInvite,
         getTracks, reassignSessionTrack, getRenderStatus, indexSessionVideo,
         planDesktopSyncQueue, startDesktopSyncQueue, getDesktopSyncQueue,
         pauseDesktopSyncQueue, resumeDesktopSyncQueue, retryDesktopSyncQueue } from '../../services/api';
import ShareModal from './ShareModal.jsx';
import EcuMatchModal from './EcuMatchModal.jsx';
import VideoMatchModal, { isVideoFile } from './VideoMatchModal.jsx';
import { mergeRenderSession } from './renderSession.js';
import WhatsNew from './WhatsNew.jsx';
import VideoRenderModal from '../VideoPanel/VideoRenderModal.jsx';
import ReassignTrackModal from '../common/ReassignTrackModal';
import useDateRangePresets, { DAY_MS, DATE_PRESETS } from '../common/useDateRangePresets';
import {
  SessionPrimaryFilters,
  SessionAdvancedFilters,
  SessionOwnershipTabs,
} from '../common/SessionBrowserControls';
import {
  SESSION_TABLE_COLS as TABLE_COLS,
  SESSION_TAB_OPTIONS,
  SESSION_SORT_COLS,
  nextSessionSort,
} from '../common/sessionBrowser';
import styles from './UploadPanel.module.css';
import HealthChip from '../CarHealth/HealthChip';
import { convertWeatherUnits } from '../../utils/unitConvert';

const CSV_EXTS   = ['.csv', '.vbo'];
const MOV_EXTS   = ['.mov'];         // legacy SmartyCAM QuickTime → /upload/video (extract_smartycam)
const SC3_EXTS   = ['.mp4'];         // SmartyCam3 → /upload/smartycam3 (XRK telemetry stream extracted via ffmpeg)
const XRK_EXTS   = ['.xrk'];         // .xrz (zlib-deflated XRK) is deliberately NOT accepted — see isXrzFile
const MOTEC_EXTS = ['.ld'];          // MoTeC i2 binary log; .ldx is an optional sidecar
const ALL_EXTS   = [...CSV_EXTS, ...MOV_EXTS, ...SC3_EXTS, ...XRK_EXTS, ...MOTEC_EXTS];

const BATCH_MAX = 200;
const XRK_BATCH_MAX = 1500;   // higher cap when every file in the batch is an XRK (e.g. a folder scan)
const BATCH_CONCURRENCY = 3; // max simultaneous uploads
const TERMINAL_RENDER_STATUSES = new Set(['completed', 'failed', 'cancelled', 'canceled', 'no_job']);

function formatSyncBytes(value) {
  const bytes = Number(value) || 0;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

function SyncStatusBadge({ item }) {
  let label = 'Not synced';
  let tone = 'idle';
  if (item) {
    if (item.status === 'imported') {
      const mode = item.sync_mode || (item.include_video ? 'source' : 'telemetry');
      label = mode === 'full'
        ? 'Full archive'
        : mode === 'ready'
          ? 'Ready to view'
          : mode === 'source' ? 'Source synced' : 'Data synced';
      tone = 'done';
    } else if (item.status === 'failed') {
      label = 'Needs attention';
      tone = 'error';
    } else if (item.status === 'waiting') {
      label = 'Waiting for connection';
      tone = 'waiting';
    } else if (item.status === 'queued') {
      label = 'Queued';
      tone = 'active';
    } else if (item.status === 'preparing') {
      label = 'Preparing';
      tone = 'active';
    } else if (item.status === 'importing') {
      label = 'Server processing';
      tone = 'active';
    } else if (item.status === 'uploading') {
      const percent = item.total_bytes
        ? Math.min(100, Math.round((item.bytes_sent / item.total_bytes) * 100))
        : 0;
      label = `Uploading ${percent}%`;
      tone = 'active';
    }
  }
  return (
    <span
      className={`${styles.syncBadge} ${styles[`syncBadge_${tone}`]}`}
      title={item?.error || label}
    >
      {label}
    </span>
  );
}

function SyncPlanDialog({ scopeLabel, plan, loading, error, onClose, onStart, onConnect }) {
  const [restoreHosted, setRestoreHosted] = useState(false);

  const modeSummary = (mode) => {
    if (!plan || !restoreHosted) {
      return {
        count: plan?.[`${mode}_pending_count`] || 0,
        bytes: plan?.[`${mode}_bytes`] || 0,
      };
    }
    const eligible = (plan.sessions || []).filter(session => session[`${mode}_ready`]);
    return {
      count: eligible.length,
      bytes: eligible.reduce((total, session) => total + (session[`${mode}_bytes`] || 0), 0),
    };
  };
  const telemetry = modeSummary('telemetry');
  const ready = modeSummary('ready');
  const full = modeSummary('full');
  const renderedBytes = restoreHosted
    ? (plan?.sessions || []).reduce(
      (total, session) => total + (session.ready_ready ? session.rendered_video_bytes || 0 : 0),
      0,
    )
    : plan?.rendered_video_bytes || 0;
  const sourceVideoBytes = restoreHosted
    ? (plan?.sessions || []).reduce(
      (total, session) => total + (session.full_ready ? session.source_video_bytes || 0 : 0),
      0,
    )
    : plan?.source_video_bytes || 0;

  return (
    <div className={styles.duplicateOverlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={styles.syncDialog} role="dialog" aria-modal="true" aria-label={scopeLabel}>
        <h3>{scopeLabel}</h3>
        {loading && <p className={styles.syncDialogHint}>Inspecting local source files…</p>}
        {error && <div className={styles.error}>⚠ {error}</div>}
        {!loading && error && !plan && (
          <div className={styles.syncDialogActions}>
            <button className={styles.filterReset} onClick={onClose}>Close</button>
          </div>
        )}
        {!loading && plan && !plan.paired && (
          <>
            <p className={styles.syncDialogHint}>
              Connect this Windows computer to your hosted TDA account before starting the queue.
            </p>
            <div className={styles.syncDialogActions}>
              <button className={styles.filterReset} onClick={onClose}>Cancel</button>
              <button className={styles.syncPrimaryBtn} onClick={onConnect}>Connect hosted account</button>
            </div>
          </>
        )}
        {!loading && plan?.paired && (
          <>
            <p className={styles.syncDialogHint}>
              Transfers run one session at a time and continue if this browser tab closes.
            </p>
            <label className={styles.syncRestoreOption}>
              <input
                type="checkbox"
                checked={restoreHosted}
                onChange={event => setRestoreHosted(event.target.checked)}
              />
              <span>
                <strong>Check and restore hosted copies</strong>
                <small>
                  Include sessions already marked synced. Existing server copies are verified
                  without retransmitting unchanged files.
                </small>
              </span>
            </label>
            <div className={styles.syncChoiceGrid}>
              <button
                className={styles.syncChoice}
                disabled={!telemetry.count}
                onClick={() => onStart('telemetry', restoreHosted)}
              >
                <strong>Telemetry only</strong>
                <span>{telemetry.count} session{telemetry.count === 1 ? '' : 's'}</span>
                <span>{restoreHosted ? 'Up to ' : ''}{formatSyncBytes(telemetry.bytes)}</span>
                <small>Telemetry and ECU sources; leave video for later.</small>
              </button>
              <button
                className={styles.syncChoice}
                disabled={!ready.count}
                onClick={() => onStart('ready', restoreHosted)}
              >
                <strong>Ready to view</strong>
                <span>{ready.count} session{ready.count === 1 ? '' : 's'}</span>
                <span>{restoreHosted ? 'Up to ' : ''}{formatSyncBytes(ready.bytes)}</span>
                <small>
                  Telemetry plus {formatSyncBytes(renderedBytes)} of locally rendered video.
                </small>
              </button>
              <button
                className={styles.syncChoice}
                disabled={!full.count}
                onClick={() => onStart('full', restoreHosted)}
              >
                <strong>Full source archive</strong>
                <span>{full.count} session{full.count === 1 ? '' : 's'}</span>
                <span>{restoreHosted ? 'Up to ' : ''}{formatSyncBytes(full.bytes)}</span>
                <small>
                  Includes {formatSyncBytes(sourceVideoBytes)} of original camera files
                  {' '}and rendered video when available.
                </small>
              </button>
            </div>
            {!restoreHosted && plan.telemetry_pending_count === 0
              && plan.ready_pending_count === 0
              && plan.full_pending_count === 0 && (
              <p className={styles.syncDialogDone}>
                Everything is marked synced. Select “Check and restore hosted copies” to verify
                or restore sessions deleted from the server.
              </p>
            )}
            {plan.ready_issue_count > 0 && (
              <p className={styles.syncDialogWarning}>
                Ready to view is unavailable for {plan.ready_issue_count} session{plan.ready_issue_count === 1 ? '' : 's'} without a complete local rendered package.
              </p>
            )}
            {plan.full_issue_count > 0 && (
              <p className={styles.syncDialogWarning}>
                {plan.full_issue_count} session{plan.full_issue_count === 1 ? '' : 's'} has an unavailable original source and cannot be fully archived.
              </p>
            )}
            <div className={styles.syncDialogActions}>
              <button className={styles.filterReset} onClick={onClose}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SyncQueueSummary({ queue, onPause, onResume, onRetry }) {
  if (!queue?.items?.length) return null;
  const summary = queue.summary || {};
  const busy = (summary.active || 0) + (summary.queued || 0) + (summary.waiting || 0) > 0;
  const percent = summary.total_bytes
    ? Math.min(100, Math.round((summary.bytes_sent / summary.total_bytes) * 100))
    : 0;
  const current = queue.current;
  return (
    <div className={styles.syncQueuePanel}>
      <div className={styles.syncQueueHead}>
        <span><CloudUpload size={14} /> Server Sync</span>
        <span>{summary.imported || 0} of {summary.total || 0} complete</span>
      </div>
      {busy && (
        <>
          <div className={styles.syncQueueProgress}><div style={{ width: `${percent}%` }} /></div>
          <p className={styles.syncQueueCurrent}>
            {queue.paused
              ? (queue.pause_reason || 'Queue paused')
              : current
                ? `${current.label} · ${current.status === 'waiting' ? 'waiting for connection' : current.status}`
                : 'Preparing queue…'}
            {' · '}{formatSyncBytes(summary.bytes_sent)} of {formatSyncBytes(summary.total_bytes)}
          </p>
        </>
      )}
      {!busy && !summary.failed && <p className={styles.syncQueueCurrent}>All queued sessions are synced.</p>}
      <div className={styles.syncQueueActions}>
        {busy && (queue.paused
          ? <button className={styles.filterReset} onClick={onResume}>Resume</button>
          : <button className={styles.filterReset} onClick={onPause}>Pause after current session</button>
        )}
        {!!summary.failed && <button className={styles.filterReset} onClick={onRetry}>Retry {summary.failed} failed</button>}
      </div>
    </div>
  );
}

// Summit Point Motorsports Park — Main Circuit
const CIRCUIT_D =
  'M 330 153 ' +
  'L 264 214 ' +
  'C 246 222 218 224 212 206 ' +
  'C 220 190 250 163 264 163 ' +
  'C 272 149 262 140 252 143 ' +
  'C 218 133 168 110 143 101 ' +
  'C 143 94 142 88 143 85 ' +
  'C 134 79 114 73 100 69 ' +
  'C 104 69 104 69 104 69 ' +
  'C 114 64 134 60 137 62 ' +
  'C 140 62 143 60 146 59 ' +
  'C 151 55 168 50 190 52 ' +
  'C 256 58 326 68 371 77 ' +
  'C 386 82 398 88 396 92 ' +
  'C 379 108 357 130 330 153 Z';

// ── 1. Animated circuit background ───────────────────────────────────────────
function CircuitBg() {
  return (
    <svg
      className={styles.circuitBg}
      viewBox="0 0 480 280"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      <defs>
        <path id="trackPath" d={CIRCUIT_D} />
        <filter id="dotGlow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="haloGlow" x="-200%" y="-200%" width="500%" height="500%">
          <feGaussianBlur stdDeviation="7" />
        </filter>
      </defs>
      <use href="#trackPath" fill="none" stroke="#ffffff" strokeWidth="18" opacity="0.04" />
      <use href="#trackPath" fill="none" stroke="#ffffff" strokeWidth="1" opacity="0.10" />
      <use href="#trackPath" fill="none" stroke="#e8411a" strokeWidth="0.5" opacity="0.18" />
      <line x1="325" y1="147" x2="335" y2="159" stroke="#ffffff" strokeWidth="2" opacity="0.3" />
      <circle r="10" fill="#e8411a" opacity="0.12" filter="url(#haloGlow)">
        <animateMotion dur="12s" repeatCount="indefinite">
          <mpath href="#trackPath" />
        </animateMotion>
      </circle>
      <circle r="4.5" fill="#e8411a" filter="url(#dotGlow)" opacity="0.95">
        <animateMotion dur="12s" repeatCount="indefinite" rotate="auto">
          <mpath href="#trackPath" />
        </animateMotion>
      </circle>
    </svg>
  );
}

// ── 2. Speed trace hero icon ──────────────────────────────────────────────────
function SpeedTraceIcon() {
  return (
    <svg className={styles.speedIcon} viewBox="0 0 200 80" aria-hidden="true">
      <path
        className={`${styles.traceLine} ${styles.traceGreen}`}
        d="M 0 62 C 22 62 30 38 45 42 C 60 46 65 64 85 50 C 105 36 120 28 142 35 C 162 42 170 56 200 53"
        fill="none" stroke="#44cc88" strokeWidth="2" strokeLinecap="round"
      />
      <path
        className={`${styles.traceLine} ${styles.traceBlue}`}
        d="M 0 56 C 20 56 28 30 42 34 C 56 38 62 60 82 42 C 102 24 118 18 138 26 C 158 34 168 50 200 46"
        fill="none" stroke="#4da6ff" strokeWidth="2" strokeLinecap="round"
      />
      <path
        className={`${styles.traceLine} ${styles.traceRed}`}
        d="M 0 50 C 20 50 25 20 40 25 C 55 30 60 55 80 35 C 100 15 115 10 135 20 C 155 30 165 45 200 40"
        fill="none" stroke="#e8411a" strokeWidth="2.5" strokeLinecap="round"
      />
    </svg>
  );
}

// ── 3. Upload progress bar ────────────────────────────────────────────────────
function UploadProgressBar({ progress, filename }) {
  return (
    <div className={styles.uploadProgress}>
      <div className={styles.uploadProgressHeader}>
        <span className={styles.uploadFilename}>{filename}</span>
        <span className={styles.uploadPct}>{progress}%</span>
      </div>
      <div className={styles.uploadBarTrack}>
        <div className={styles.uploadBarFill} style={{ width: `${progress}%` }} />
      </div>
      <span className={styles.uploadStatus}>Uploading…</span>
    </div>
  );
}

// ── 4. Video converting — spinning film reels + elapsed timer ────────────────
function VideoConvertingLoader() {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, []);
  const mins = Math.floor(elapsed / 60);
  const secs = String(elapsed % 60).padStart(2, '0');
  return (
    <div className={styles.videoConvert}>
      <svg className={styles.reelSvg} viewBox="0 0 200 80" aria-hidden="true">
        <rect x="69" y="35" width="62" height="10" fill="#111" stroke="#2a2a2a" strokeWidth="0.5"/>
        {[81, 93, 105, 117, 129].map(x => (
          <line key={x} x1={x} y1="35" x2={x} y2="45" stroke="#333" strokeWidth="0.5"/>
        ))}
        {[75, 99, 123].map(x => (
          <rect key={`t${x}`} x={x - 3} y="32" width="5" height="3" rx="0.5" fill="#111" stroke="#333" strokeWidth="0.5"/>
        ))}
        {[75, 99, 123].map(x => (
          <rect key={`b${x}`} x={x - 3} y="45" width="5" height="3" rx="0.5" fill="#111" stroke="#333" strokeWidth="0.5"/>
        ))}
        <g transform="translate(45,40)">
          <g className={styles.reelLeft}>
            <circle r="25" fill="#0f0f0f" stroke="#e8411a" strokeWidth="1.2" opacity="0.7"/>
            <circle r="8" fill="#1a0800" stroke="#e8411a" strokeWidth="0.8"/>
            {[0, 120, 240].map(deg => {
              const rad = (deg * Math.PI) / 180;
              return <line key={deg} x1={Math.sin(rad)*9} y1={-Math.cos(rad)*9} x2={Math.sin(rad)*22} y2={-Math.cos(rad)*22} stroke="#e8411a" strokeWidth="1.8" strokeLinecap="round" opacity="0.65"/>;
            })}
            {[0,45,90,135,180,225,270,315].map(deg => {
              const rad = (deg * Math.PI) / 180;
              return <circle key={deg} cx={Math.sin(rad)*21} cy={-Math.cos(rad)*21} r="2.2" fill="#0a0a0a" stroke="#e8411a" strokeWidth="0.5" opacity="0.55"/>;
            })}
          </g>
        </g>
        <g transform="translate(155,40)">
          <g className={styles.reelRight}>
            <circle r="19" fill="#0f0f0f" stroke="#e8411a" strokeWidth="1.2" opacity="0.7"/>
            <circle r="6" fill="#1a0800" stroke="#e8411a" strokeWidth="0.8"/>
            {[0, 120, 240].map(deg => {
              const rad = (deg * Math.PI) / 180;
              return <line key={deg} x1={Math.sin(rad)*7} y1={-Math.cos(rad)*7} x2={Math.sin(rad)*16} y2={-Math.cos(rad)*16} stroke="#e8411a" strokeWidth="1.5" strokeLinecap="round" opacity="0.65"/>;
            })}
            {[0,60,120,180,240,300].map(deg => {
              const rad = (deg * Math.PI) / 180;
              return <circle key={deg} cx={Math.sin(rad)*15.5} cy={-Math.cos(rad)*15.5} r="1.8" fill="#0a0a0a" stroke="#e8411a" strokeWidth="0.5" opacity="0.55"/>;
            })}
          </g>
        </g>
      </svg>
      <span className={styles.videoConvertMsg}>Converting video…</span>
      <span className={styles.videoConvertElapsed}>{mins}:{secs} elapsed</span>
    </div>
  );
}

// ── 5. F1 lights-out loading animation ───────────────────────────────────────
function LightsOutLoader({ message }) {
  return (
    <div className={styles.lightsContainer}>
      <div className={styles.lightsGantry}>
        {[1, 2, 3, 4, 5].map(n => (
          <div key={n} className={styles.lightSocket}>
            <div className={`${styles.light} ${styles[`light${n}`]}`} />
          </div>
        ))}
      </div>
      <span className={styles.lightsMsg}>{message}</span>
    </div>
  );
}

// ── 6. Duplicate file dialog ──────────────────────────────────────────────────
function DuplicateDialog({ info, onOpenExisting, onUploadAnyway, onDismiss }) {
  return (
    <div className={styles.duplicateOverlay}>
      <div className={styles.duplicateDialog}>
        <p className={styles.duplicateTitle}>File already uploaded</p>
        <p className={styles.duplicateBody}>
          This file matches an existing session:{' '}
          <span className={styles.duplicateSessionName}>{info.existingSessionName || 'Unknown session'}</span>.
          Open it, or upload a duplicate anyway.
        </p>
        <div className={styles.duplicateActions}>
          <button className={styles.duplicateBtnPrimary} onClick={onOpenExisting}>Open existing session</button>
          <button className={styles.duplicateBtnSecondary} onClick={onUploadAnyway}>Upload anyway</button>
        </div>
        <button className={styles.duplicateBtnDismiss} onClick={onDismiss}>Cancel</button>
      </div>
    </div>
  );
}

// ── 7. Batch queue item ───────────────────────────────────────────────────────
const QUEUE_ICONS = { queued: '○', uploading: '◉', processing: '◉', done: '✓', skipped: '⊘', error: '✗' };

function BatchQueueItem({ item }) {
  return (
    <div className={`${styles.batchItem} ${styles[`batchStatus_${item.status}`]}`}>
      <span className={`${styles.batchIcon} ${item.status === 'processing' ? styles.batchIconPulse : ''}`}>
        {QUEUE_ICONS[item.status]}
      </span>
      <div className={styles.batchItemBody}>
        <span className={styles.batchFilename}>{item.file.name}</span>
        {item.status === 'uploading' && (
          <div className={styles.batchBar}>
            <div className={styles.batchBarFill} style={{ width: `${item.progress}%` }} />
          </div>
        )}
        {item.status === 'processing' && (
          <span className={styles.batchProcessing}>Processing on server…</span>
        )}
        {item.status === 'skipped' && item.skippedAs && (
          <span className={styles.batchNote}>Already uploaded as "{item.skippedAs.name}"</span>
        )}
        {item.status === 'error' && item.errorMsg && (
          <span className={styles.batchError}>{item.errorMsg}</span>
        )}
      </div>
    </div>
  );
}

// ── 8. Batch queue panel ──────────────────────────────────────────────────────
function BatchQueuePanel({ queue, onClear }) {
  if (!queue.length) return null;
  const terminal = queue.filter(i => ['done', 'error', 'skipped'].includes(i.status));
  const allDone = terminal.length === queue.length;
  const uploaded = queue.filter(i => i.status === 'done').length;
  const skipped  = queue.filter(i => i.status === 'skipped').length;
  const failed   = queue.filter(i => i.status === 'error').length;

  return (
    <div className={styles.batchPanel}>
      <div className={styles.batchPanelHeader}>
        <span className={styles.batchPanelTitle}>Upload Queue</span>
        <span className={styles.batchPanelCount}>{terminal.length}/{queue.length}</span>
        {allDone && (
          <button className={styles.batchClearBtn} onClick={onClear}>Clear</button>
        )}
      </div>
      <div className={styles.batchList}>
        {queue.map(item => <BatchQueueItem key={item.id} item={item} />)}
      </div>
      {allDone && (
        <div className={styles.batchSummary}>
          {uploaded > 0 && <span className={styles.summaryDone}>{uploaded} uploaded</span>}
          {skipped  > 0 && <span className={styles.summarySkipped}>{skipped} already existed</span>}
          {failed   > 0 && <span className={styles.summaryFailed}>{failed} failed</span>}
        </div>
      )}
    </div>
  );
}

// ── 9. Multi-file upload confirmation modal ─────────────────────────────────────
// Staged before enqueueFiles(): lets the user review (and curate) the full set of
// files a multi-select or folder scan collected before any upload starts. Parsing is
// all-or-nothing server-side, so we show client-side info only (type, name, size).
const KIND_LABELS = { csv: 'CSV', video: 'MOV', smartycam3: 'MP4', xrk: 'XRK', motec: 'LD' };

function fmtBytes(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = bytes / 1024, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 ? 0 : 1)} ${units[i]}`;
}

function UploadConfirmModal({ files, onConfirm, onCancel }) {
  // Index-based ids are stable here — the file list is static for the modal's lifetime.
  const rows = useMemo(() => files.map((f, i) => ({
    id: i,
    file: f,
    kind: classifyFile(f),
    isSidecar: f.name.toLowerCase().endsWith('.ldx'),
    path: f.webkitRelativePath || f._relPath || f.name,
    mtime: f.lastModified || 0, // filesystem last-modified; proxy for the file's date
  })), [files]);
  const [removed, setRemoved] = useState(() => new Set());
  const [sort, setSort] = useState({ col: 'name', dir: 'asc' }); // name A→Z by default
  const [disabledKinds, setDisabledKinds] = useState(() => new Set()); // empty = all types shown
  const [minSizeKB, setMinSizeKB] = useState('');
  const [maxAgeDays, setMaxAgeDays] = useState('');
  const [nameFilter, setNameFilter] = useState('');

  const sortLabel = (r) => r.isSidecar ? 'LDX' : (KIND_LABELS[r.kind] || '—');
  const compare = {
    type: (a, b) => sortLabel(a).localeCompare(sortLabel(b)) || a.path.localeCompare(b.path),
    name: (a, b) => a.path.localeCompare(b.path),
    date: (a, b) => a.mtime - b.mtime,
    size: (a, b) => (a.file.size || 0) - (b.file.size || 0),
  };
  // Size and date default to desc (biggest / newest first); name and type to asc.
  const toggleSort = (col) => setSort(prev =>
    prev.col === col
      ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { col, dir: (col === 'size' || col === 'date') ? 'desc' : 'asc' });

  // Distinct uploadable kinds in the batch — the type filter only appears when >1.
  const presentKinds = [...new Set(rows.filter(r => r.kind && !r.isSidecar).map(r => r.kind))];
  const minBytes = minSizeKB ? parseFloat(minSizeKB) * 1024 : 0;
  const maxAgeMs = maxAgeDays ? parseFloat(maxAgeDays) * 86400000 : null;
  const nameNeedle = nameFilter.trim().toLowerCase();
  const filtersActive = disabledKinds.size > 0 || !!minSizeKB || !!maxAgeDays || !!nameNeedle;
  // Sidecars always pass — they follow their .ld; enqueueFiles pairs/drops them.
  const passesFilter = (r) => {
    if (r.isSidecar) return true;
    if (r.kind && disabledKinds.has(r.kind)) return false;
    if ((r.file.size || 0) < minBytes) return false;
    if (maxAgeMs != null && r.mtime && (Date.now() - r.mtime) > maxAgeMs) return false;
    if (nameNeedle && !r.path.toLowerCase().includes(nameNeedle)) return false;
    return true;
  };
  const toggleKind = (k) => setDisabledKinds(prev => {
    const next = new Set(prev); next.has(k) ? next.delete(k) : next.add(k); return next;
  });
  const clearFilters = () => { setDisabledKinds(new Set()); setMinSizeKB(''); setMaxAgeDays(''); setNameFilter(''); };

  const base = rows.filter(r => !removed.has(r.id));
  const kept = base
    .filter(passesFilter)
    .sort((a, b) => sort.dir === 'asc' ? compare[sort.col](a, b) : compare[sort.col](b, a));
  const hiddenByFilter = base.length - kept.length;
  // Sidecars (.ldx) and unsupported files are not standalone uploads — enqueueFiles
  // pairs/drops them — so the headline count reflects only the real uploads.
  const uploadable = kept.filter(r => r.kind && !r.isSidecar);
  const cap = uploadable.length > 0 && uploadable.every(r => isXrkFile(r.file)) ? XRK_BATCH_MAX : BATCH_MAX;
  const overCap = uploadable.length > cap;
  const totalBytes = kept.reduce((s, r) => s + (r.file.size || 0), 0);

  const byKind = {};
  for (const r of uploadable) byKind[r.kind] = (byKind[r.kind] || 0) + 1;
  const summary = Object.entries(byKind).map(([k, n]) => `${n} ${KIND_LABELS[k] || k}`).join(' · ');

  const remove = (id) => setRemoved(prev => new Set(prev).add(id));

  return (
    <div className={styles.duplicateOverlay} onClick={e => e.target === e.currentTarget && onCancel()}>
      <div className={styles.confirmModal}>
        <div className={styles.confirmHeader}>
          <p className={styles.duplicateTitle}>
            Review {uploadable.length} file{uploadable.length === 1 ? '' : 's'} to upload
          </p>
          <button className={styles.confirmClose} onClick={onCancel} title="Cancel">×</button>
        </div>
        <p className={styles.confirmSummary}>
          {summary || 'No uploadable files'}{summary ? ` · ${fmtBytes(totalBytes)}` : ''}
          {hiddenByFilter > 0 && (
            <span className={styles.confirmHidden}> · {hiddenByFilter} hidden by filter</span>
          )}
        </p>
        {overCap && (
          <div className={styles.batchWarning}>
            ⚠ Only the first {cap} files will be queued ({uploadable.length - cap} won't).
          </div>
        )}
        <div className={styles.confirmFilterBar}>
          {presentKinds.length > 1 && (
            <div className={styles.confirmChips}>
              {presentKinds.map(k => (
                <button
                  key={k}
                  className={`${styles.confirmChip} ${disabledKinds.has(k) ? styles.confirmChipOff : ''}`}
                  onClick={() => toggleKind(k)}
                  title={disabledKinds.has(k) ? `Show ${KIND_LABELS[k]}` : `Hide ${KIND_LABELS[k]}`}
                >
                  {KIND_LABELS[k] || k}
                </button>
              ))}
            </div>
          )}
          <label className={styles.confirmFilterField}>
            name
            <input
              type="text" placeholder="contains…"
              className={styles.confirmNameInput}
              value={nameFilter}
              onChange={e => setNameFilter(e.target.value)}
            />
          </label>
          <label className={styles.confirmFilterField}>
            ≥
            <input
              type="number" min="0" step="any" placeholder="0"
              className={styles.confirmFilterInput}
              value={minSizeKB}
              onChange={e => setMinSizeKB(e.target.value)}
            />
            KB
          </label>
          <label className={styles.confirmFilterField}>
            newer than
            <input
              type="number" min="0" step="any" placeholder="∞"
              className={styles.confirmFilterInput}
              value={maxAgeDays}
              onChange={e => setMaxAgeDays(e.target.value)}
            />
            days
          </label>
          {filtersActive && (
            <button className={styles.filterClear} onClick={clearFilters} title="Clear filters">Clear</button>
          )}
        </div>
        <div className={styles.confirmSortBar}>
          {[['type', 'Type'], ['name', 'Name'], ['date', 'Date'], ['size', 'Size']].map(([col, label]) => (
            <button
              key={col}
              className={[
                styles.confirmSortBtn,
                col === 'name' && styles.confirmSortName,
                col === 'date' && styles.confirmSortDate,
                col === 'size' && styles.confirmSortSize,
                sort.col === col && styles.confirmSortActive,
              ].filter(Boolean).join(' ')}
              onClick={() => toggleSort(col)}
            >
              {label}{sort.col === col ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
            </button>
          ))}
          <span className={styles.confirmSortSpacer} />
        </div>
        <div className={styles.confirmList}>
          {kept.length === 0 && (
            <div className={styles.confirmEmpty}>
              {hiddenByFilter > 0 ? 'No files match the current filters.' : 'All files removed.'}
            </div>
          )}
          {kept.map(r => (
            <div key={r.id} className={styles.confirmItem}>
              <span className={styles.badge}>{r.isSidecar ? 'LDX' : (KIND_LABELS[r.kind] || '—')}</span>
              <div className={styles.batchItemBody}>
                <span className={styles.batchFilename} title={r.path}>{r.path}</span>
                {r.isSidecar && <span className={styles.batchNote}>Paired sidecar — uploaded with its .ld</span>}
                {!r.kind && !r.isSidecar && <span className={styles.batchError}>Unsupported — will be skipped</span>}
              </div>
              <span className={styles.confirmDate}>{r.mtime ? fmtDate(r.mtime) : '—'}</span>
              <span className={styles.confirmSize}>{fmtBytes(r.file.size)}</span>
              <button className={styles.confirmRemove} onClick={() => remove(r.id)} title="Remove">×</button>
            </div>
          ))}
        </div>
        <div className={styles.duplicateActions}>
          <button
            className={styles.duplicateBtnPrimary}
            disabled={uploadable.length === 0}
            onClick={() => onConfirm(kept.map(r => r.file))}
          >
            Upload {uploadable.length} file{uploadable.length === 1 ? '' : 's'}
          </button>
          <button className={styles.duplicateBtnSecondary} onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── 9. Event group header ─────────────────────────────────────────────────────
// `compact` is the table-view dress: tighter, and it carries the aggregate stats
// inline because a table row has no room beneath it for card view's stats bar.
function EventGroupHeader({ group, collapsed, onToggle, onRename, onShareGroup, isGroupShared, onDeleteGroup, compact, stats, selectState, onToggleSelect }) {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft]       = useState('');

  const displayTitle = group.label || getGroupDefaultName(group);
  const dateRange    = formatGroupDateRange(group);

  // `indeterminate` is a DOM property, not an attribute, so React can't set it
  // declaratively — a callback ref keeps it in step with the current state.
  const selectBoxRef = useCallback((el) => {
    if (el) el.indeterminate = selectState === 'some';
  }, [selectState]);

  const startRename = (e) => {
    e.stopPropagation();
    setDraft(group.label || displayTitle);
    setRenaming(true);
  };

  const commitRename = () => {
    const val = draft.trim();
    if (val) onRename(val);
    setRenaming(false);
  };

  return (
    <div
      className={compact ? `${styles.groupHeader} ${styles.groupHeaderCompact}` : styles.groupHeader}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
    >
      {/* Selecting an event is a shortcut for selecting its sessions — the
          selection stays a set of session ids, so Delete/Sync/Report need no
          notion of an event. Clicks must not reach the header's collapse. */}
      {onToggleSelect && (
        <input
          ref={selectBoxRef}
          type="checkbox"
          className={styles.groupSelectBox}
          checked={selectState === 'all'}
          onClick={e => e.stopPropagation()}
          onChange={onToggleSelect}
          onKeyDown={e => e.stopPropagation()}
          aria-label={`Select all sessions in ${displayTitle}`}
          title={selectState === 'all' ? 'Deselect this event' : 'Select every session in this event'}
        />
      )}
      <span className={styles.groupToggle}>{collapsed ? '▶' : '▼'}</span>

      {/* Swallow clicks only while the rename input is open — otherwise this
          area is `flex: 1`, i.e. most of the header, and eating its clicks
          leaves the twisty as the only way to collapse an event. */}
      <div className={styles.groupLabelArea} onClick={renaming ? (e => e.stopPropagation()) : undefined}>
        {renaming ? (
          <input
            className={styles.groupRenameInput}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={e => {
              e.stopPropagation(); // prevent Space from toggling group collapse
              if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
              if (e.key === 'Escape') { e.preventDefault(); setRenaming(false); }
            }}
            autoFocus
          />
        ) : (
          <>
            <span className={styles.groupTitle}>{displayTitle}</span>
            {dateRange && <span className={styles.groupDate}>{dateRange}</span>}
          </>
        )}
      </div>

      <div className={styles.groupHeaderRight}>
        {compact && stats && (
          <span className={styles.groupInlineStats}>
            {stats.bestLap && <span>Best <strong className={styles.groupStatValue}>{stats.bestLap.time}</strong></span>}
            {stats.totalLaps > 0 && <span><strong className={styles.groupStatValue}>{stats.totalLaps}</strong> laps</span>}
            {stats.totalDuration > 0 && <span><strong className={styles.groupStatValue}>{fmtDuration(stats.totalDuration)}</strong></span>}
          </span>
        )}
        <span className={styles.groupCount}>{group.sessions.length} session{group.sessions.length !== 1 ? 's' : ''}</span>
        {!renaming && onShareGroup && (
          <button
            className={isGroupShared ? styles.groupSharedBtn : styles.groupShareBtn}
            onClick={e => { e.stopPropagation(); onShareGroup(); }}
            title={isGroupShared ? 'Manage sharing for this event' : 'Share all sessions in this event'}
            aria-label={isGroupShared ? 'Manage event sharing' : 'Share event'}
          >{isGroupShared ? '↗ Shared event' : '↗ Share event'}</button>
        )}
        {!renaming && (
          <button
            className={styles.groupRenameBtn}
            onClick={startRename}
            title="Rename event"
            aria-label="Rename event"
          >✎</button>
        )}
        {!renaming && onDeleteGroup && (
          <button
            className={styles.groupDeleteBtn}
            onClick={e => { e.stopPropagation(); onDeleteGroup(); }}
            title="Delete all sessions in this event"
            aria-label="Delete event"
          >×</button>
        )}
      </div>
    </div>
  );
}


// ── Table view ─────────────────────────────────────────────────────────────────
// Keys match the backend's SORT_COLUMNS (api/sessions.py) exactly — sorting is
// server-side now, so a column is only clickable here if the server supports it.
// `col` is the colgroup class carrying that column's width — the table is
// `table-layout: fixed` (see UploadPanel.module.css) so these widths, not the
// cell text, decide the layout. Track is deliberately width-less: it takes
// whatever the window has left over.
// Session-list filters are deliberately tab-local. Opening a session unmounts
// UploadPanel (and the in-app Session List action currently reloads the page),
// so React state alone cannot survive the round trip. sessionStorage preserves
// that navigation context without turning a one-off search into a permanent
// account preference. The user id keeps masqueraded/account switches isolated.
const SESSION_FILTERS_VERSION = 1;
const SESSION_FILTERS_KEY = 'ta_sessionListFilters';
const SESSION_TABS = new Set(SESSION_TAB_OPTIONS.map(({ key }) => key));
const SESSION_CONDITIONS = new Set(['all', 'dry', 'wet']);
const SESSION_DATE_FIELDS = new Set(['session', 'uploaded']);
const SESSION_DATE_PRESETS = new Set(DATE_PRESETS.map(([key]) => key));

function validRange(value) {
  return Array.isArray(value) && value.length === 2
    && value.every(v => typeof v === 'number' && Number.isFinite(v))
    && value[0] <= value[1];
}

function sessionFiltersKey(userId) {
  return `${SESSION_FILTERS_KEY}:${userId || 'current'}`;
}

function readSessionFilters(userId) {
  try {
    const value = JSON.parse(sessionStorage.getItem(sessionFiltersKey(userId)) || 'null');
    if (!value || value.version !== SESSION_FILTERS_VERSION) return {};
    const sort = value.sort;
    return {
      filterText: typeof value.filterText === 'string' ? value.filterText : '',
      filterTrack: typeof value.filterTrack === 'string' ? value.filterTrack : '',
      filterCar: typeof value.filterCar === 'string' ? value.filterCar : '',
      filterDriver: typeof value.filterDriver === 'string' ? value.filterDriver : '',
      tempRange: validRange(value.tempRange) ? value.tempRange : null,
      tempUnit: value.tempUnit === 'C' || value.tempUnit === 'F' ? value.tempUnit : null,
      dateField: SESSION_DATE_FIELDS.has(value.dateField) ? value.dateField : 'session',
      // A zero-width date window matches only sessions at that exact instant,
      // i.e. nothing. It comes from both slider handles collapsing, so restoring
      // it just replays a filter the user never meant to set — drop it.
      dateRange: validRange(value.dateRange) && value.dateRange[0] < value.dateRange[1]
        ? value.dateRange : null,
      datePreset: SESSION_DATE_PRESETS.has(value.datePreset) ? value.datePreset : null,
      conditions: SESSION_CONDITIONS.has(value.conditions) ? value.conditions : 'all',
      activeTab: SESSION_TABS.has(value.activeTab) ? value.activeTab : 'all',
      sort: sort && SESSION_SORT_COLS.has(sort.col) && (sort.dir === 'asc' || sort.dir === 'desc')
        ? sort
        : { col: 'date', dir: 'desc' },
    };
  } catch {
    return {};
  }
}

function writeSessionFilters(userId, value) {
  try {
    sessionStorage.setItem(sessionFiltersKey(userId), JSON.stringify({
      version: SESSION_FILTERS_VERSION,
      ...value,
    }));
  } catch {
    // Storage can be unavailable in locked-down/private browser contexts. The
    // list remains fully usable; it simply falls back to its existing defaults.
  }
}

function convertTempRange(range, fromUnit, toUnit) {
  if (!validRange(range) || !fromUnit || fromUnit === toUnit) return range;
  return range.map(v => fromUnit === 'F' ? (v - 32) * 5 / 9 : v * 9 / 5 + 32);
}

function SessionTable({ sessions, sort, onSort, trackBests, unitPrefs, onLoadSession, loading, deletingId, sharedByMeIds, renderingIds, onShare, onDelete, onRender, onRenderStatus, onReassign, onIndexVideo, indexingId, isAdmin, selectedIds, onSelectClick, onToggleSelectAll, grouped, groups, collapsedGroups, groupHeaderProps, desktopSync, syncItemsBySession }) {
  const isTrackBest = (s) => {
    const track = trackKey(s);
    return track && s.best_lap_seconds != null && trackBests[track] != null && s.best_lap_seconds <= trackBests[track] + 0.001;
  };
  // One render plan drives both the markup and the selection list — see
  // buildTableRows on why they must not be derived separately.
  const rows = grouped
    ? buildTableRows(groups, collapsedGroups)
    : sessions.map(s => ({ kind: 'session', session: s }));

  const selectableIds = rows
    .filter(r => r.kind === 'session')
    .map(r => r.session.session_id);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selectedIds.has(id));
  const shiftKeyRef = useRef(false); // captured on checkbox onClick, read by onChange (see UploadPanel's own copy for why)

  return (
    <div className={styles.tableWrapper}>
      <table className={styles.sessionTable}>
        <colgroup>
          <col className={styles.colSelect} />
          {TABLE_COLS.map((c, i) => <col key={i} className={c.col ? styles[c.col] : undefined} />)}
        </colgroup>
        <thead>
          <tr>
            <th className={styles.tdMono}>
              {selectableIds.length > 0 && (
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={() => onToggleSelectAll(selectableIds)}
                  aria-label="Select all sessions"
                  title="Select all"
                />
              )}
            </th>
            {TABLE_COLS.map((c, i) => {
              if (!c.key) return <th key={i} className={i === TABLE_COLS.length - 1 ? styles.tdActionsCell : undefined}>{c.label}</th>;
              const active = sort.col === c.key;
              return (
                <th key={i}
                  className={`${styles.thSortable} ${active ? styles.thSorted : ''}`}
                  onClick={() => onSort(c.key)}
                  title="Click to sort"
                >
                  {c.label}
                  {active && (
                    <span className={styles.sortBadge}>{sort.dir === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            if (row.kind === 'header') {
              const { group, collapsed, rail } = row;
              return (
                <tr key={`event-${group.key}`} className={row.groupEnd ? `${styles.groupRow} ${styles.groupEnd}` : styles.groupRow}>
                  <td className={`${styles.groupRowCell} ${styles[rail]}`} colSpan={TABLE_COLS.length + 1}>
                    <EventGroupHeader
                      compact
                      group={group}
                      collapsed={collapsed}
                      stats={groupStats(group)}
                      {...groupHeaderProps(group)}
                    />
                  </td>
                </tr>
              );
            }

            const s = row.session;
            const disabled = !s.can_reload || !!loading || deletingId === s.session_id;
            const trackBest = isTrackBest(s);
            let weatherStr = '—';
            if (s.weather) {
              const w = convertWeatherUnits(s.weather, unitPrefs || { temp: 'F', speed: 'mph', pressure: 'psi' });
              weatherStr = `${weatherIcon(s.weather.condition)} ${w.temp ?? '—'}`;
            }
            return (
              <tr
                key={s.session_id}
                className={[
                  disabled && styles.trDisabled,
                  row.rail && styles[row.rail],
                  row.groupEnd && styles.groupEnd,
                ].filter(Boolean).join(' ')}
                onClick={() => !disabled && onLoadSession && onLoadSession(s.session_id, s.can_comment !== false, s.is_owner !== false)}
                title={!s.can_reload ? 'Original CSV not available — re-upload to restore' : undefined}
              >
                <td className={styles.tdMono} onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(s.session_id)}
                    onClick={e => { e.stopPropagation(); shiftKeyRef.current = e.shiftKey; }}
                    onChange={() => onSelectClick(s.session_id, selectableIds, shiftKeyRef.current)}
                    aria-label={`Select session ${trackDisplayName(s) || ''}`}
                  />
                </td>
                <td className={styles.tdTrack} title={trackDisplayName(s) || 'Session'}>
                  {trackDisplayName(s) || 'Session'}
                  {trackBest && <span className={styles.tblTrackBadge} title="Fastest lap at this track">⚑</span>}
                  {(s.has_video || s.has_rendered_video) && <span className={styles.tblVideoBadge}><Film size={12} /></span>}
                  {s.has_ecu_data && <span className={styles.tblVideoBadge} title="Has merged ECU data">⚙</span>}
                  <HealthChip status={s.health_status} findings={s.health_findings} unchecked={s.health_unchecked} compact className={styles.tblHealthChip} />
                  {desktopSync && <SyncStatusBadge item={syncItemsBySession.get(s.session_id)} />}
                </td>
                <td className={styles.tdMono}
                    title={s.date ? `${s.date}${s.time ? ` ${fmtTime12(s.time)}` : ''}` : undefined}>
                  {s.date ? `${shortYear(s.date)}${s.time ? ` ${fmtTime12(s.time)}` : ''}`
                          : (fmtDateTimeCompact(s.created_at) || '—')}
                </td>
                <td className={styles.tdLap}>{s.best_lap_time || '—'}</td>
                <td className={styles.tdMono}>{s.lap_count ?? '—'}</td>
                <td className={styles.tdMono}>{fmtDuration(s.duration)}</td>
                <td title={s.racer || undefined}>{s.racer || '—'}</td>
                <td title={s.vehicle || undefined}>{s.vehicle || '—'}</td>
                <td className={styles.tdMono}>{(s.has_video || s.has_rendered_video) ? <Film size={12} /> : '—'}</td>
                <td>{weatherStr}</td>
                <td className={styles.tdMono} title={fmtDateTime(s.created_at) || undefined}>
                  {fmtDateTimeCompact(s.created_at) || '—'}
                </td>
                <td className={styles.tdActionsCell} onClick={e => e.stopPropagation()}>
                  <div className={styles.tdActions}>
                    {s.is_shared && !isAdmin ? (
                      <span className={styles.tblSharedBadge}>↗ Shared</span>
                    ) : (
                      <>
                        {onShare && !s.is_shared && (sharedByMeIds.has(s.session_id)
                          ? <button className={styles.sharedByMeBadge} onClick={e => { e.stopPropagation(); onShare(s); }} title="Shared — click to manage">↗</button>
                          : <button className={styles.shareBtn}        onClick={e => { e.stopPropagation(); onShare(s); }} title="Share session">↗</button>
                        )}
                        {isAdmin && (
                          <button className={styles.shareBtn} onClick={e => { e.stopPropagation(); onReassign(s); }} title="Reassign track (admin)" aria-label="Reassign track">↪</button>
                        )}
                        {isAdmin && s.has_rendered_video && (
                          <button
                            className={styles.shareBtn}
                            onClick={e => { e.stopPropagation(); onIndexVideo(s); }}
                            disabled={indexingId === s.session_id}
                            title={'Index this rendered video for search (admin). '
                                 + 'Only do this once you have confirmed the overlay '
                                 + 'lines up — a wrong sync attaches telemetry to the '
                                 + 'wrong frames. No re-render needed.'}
                            aria-label="Index video for search"
                          >{indexingId === s.session_id ? '…' : '⌕'}</button>
                        )}
                        {s.render_failed ? (
                          <button className={styles.renderFailedBadge} title="Render failed — click for details / retry" onClick={e => { e.stopPropagation(); onRenderStatus(s); }}>⚠</button>
                        ) : (s.is_rendering || renderingIds.has(s.session_id)) ? (
                          <button className={styles.renderingBadge} title="Rendering… click to view status" onClick={e => { e.stopPropagation(); onRenderStatus(s); }}>⏳</button>
                        ) : (
                          <button className={styles.addVideoBtn} onClick={e => { e.stopPropagation(); onRender(s); }} title={(s.has_rendered_video || s.has_gopro_files) ? 'Re-render overlay video' : 'Add overlay video'}><Film size={12} /></button>
                        )}
                        <button
                          className={styles.deleteBtn}
                          onClick={e => { e.stopPropagation(); onDelete(e, s.session_id); }}
                          disabled={deletingId === s.session_id}
                          title="Delete session"
                          aria-label="Delete session"
                        >{deletingId === s.session_id ? '…' : '×'}</button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Utilities ─────────────────────────────────────────────────────────────────
function classifyFile(file) {
  if (!file) return null;
  const lower = file.name.toLowerCase();
  if (CSV_EXTS.some(e => lower.endsWith(e))) return 'csv';
  if (MOV_EXTS.some(e => lower.endsWith(e))) return 'video';
  if (SC3_EXTS.some(e => lower.endsWith(e))) return 'smartycam3';
  if (XRK_EXTS.some(e => lower.endsWith(e))) return 'xrk';
  // Match .ld but not .ldx (the sidecar is paired onto its .ld, never uploaded alone)
  if (lower.endsWith('.ld')) return 'motec';
  return null;
}

function isXrkFile(file) {
  const lower = file.name.toLowerCase();
  return XRK_EXTS.some(e => lower.endsWith(e));
}

// RaceStudio writes a compressed .xrz twin beside every .xrk, so a folder drag
// used to queue both. .xrz uploads are no longer ingested; this only exists so
// the UI can say why a file was skipped rather than calling it "unsupported".
function isXrzFile(file) {
  return (file?.name || '').toLowerCase().endsWith('.xrz');
}
const XRZ_REJECT_MSG = '.xrz files are no longer accepted — export the .xrk from RaceStudio instead.';

function noXrkFoundMessage(where, xrzCount) {
  const base = `No XRK files found in the ${where} folder.`;
  return xrzCount > 0
    ? `${base} ${xrzCount} .xrz file${xrzCount === 1 ? ' was' : 's were'} skipped — .xrz is no longer accepted; export the .xrk from RaceStudio instead.`
    : base;
}

// Files the main dropzone should hand to the ECU-match flow instead of trying
// to ingest as a session. Deliberately narrower than what that flow *accepts*:
// a `.csv` is ambiguous (an AiM or RaceChrono session export looks the same to
// a filename check), so only unmistakably-ECU names auto-route. Everything else
// reaches the flow through the "Match ECU logs" button, where the user has
// already said what the file is.
function isEcuOnlyFile(file) {
  const lower = (file?.name || '').toLowerCase();
  return lower.endsWith('.maxxecu-zip-log')
    || lower.endsWith('.maxxecu-log')
    || lower.endsWith('.maxxlog');
}

// Recursively walk a dropped/selected FileSystemEntry tree, collecting only XRK
// files. readEntries returns at most 100 entries per call, so a directory reader
// must be drained in a loop until it yields an empty batch. `skipped.xrz` counts
// the compressed twins passed over so the caller can explain an empty result.
function collectXrkFromEntry(entry, out, skipped = { xrz: 0 }) {
  return new Promise((resolve) => {
    if (entry.isFile) {
      entry.file(
        file => {
          if (isXrkFile(file)) {
            // DataTransfer files lack webkitRelativePath; stash the entry path so the
            // confirm modal can show the same folder context the folder picker gives.
            if (entry.fullPath) { try { file._relPath = entry.fullPath.replace(/^\//, ''); } catch { /* read-only File */ } }
            out.push(file);
          } else if (isXrzFile(file)) {
            skipped.xrz += 1;
          }
          resolve();
        },
        () => resolve(),
      );
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      const readBatch = () => reader.readEntries(
        async (entries) => {
          if (!entries.length) { resolve(); return; }
          await Promise.all(entries.map(e => collectXrkFromEntry(e, out, skipped)));
          readBatch();
        },
        () => resolve(),
      );
      readBatch();
    } else {
      resolve();
    }
  });
}

function fmtDuration(seconds) {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

function fmtDate(isoStr) {
  if (!isoStr) return '';
  try {
    const d = new Date(isoStr);
    if (isNaN(d)) return isoStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return isoStr;
  }
}

function fmtDateTime(isoStr) {
  if (!isoStr) return '';
  try {
    const d = new Date(isoStr);
    if (isNaN(d)) return isoStr;
    const datePart = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${datePart} ${timePart}`;
  } catch {
    return isoStr;
  }
}

// Table-view variant of fmtDateTime: numeric, two-digit year. The table gives
// every column a fixed width (see the colgroup) and a clipped timestamp is
// unreadable, so this trades the spelled-out month and the century — neither of
// which anyone reads off an upload stamp — for a column that always fits.
function fmtDateTimeCompact(isoStr) {
  if (!isoStr) return '';
  try {
    const d = new Date(isoStr);
    if (isNaN(d)) return isoStr;
    const datePart = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
    const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${datePart} ${timePart}`;
  } catch {
    return isoStr;
  }
}

// "07/04/2026" → "07/04/26"; anything else is passed through untouched.
function shortYear(dateStr) {
  return typeof dateStr === 'string' ? dateStr.replace(/\/(\d{2})(\d{2})$/, '/$2') : dateStr;
}

function fmtTime12(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

function weatherIcon(condition) {
  if (!condition) return '🌡';
  const c = condition.toLowerCase();
  if (c.includes('rain'))          return '🌧';
  if (c.includes('overcast'))      return '☁';
  if (c.includes('mostly cloudy')) return '☁';
  if (c.includes('partly'))        return '⛅';
  return '☀';
}

// ── Grouping utilities ────────────────────────────────────────────────────────
function parseDateMs(session) {
  const raw = session.date || session.created_at;
  if (!raw) return 0;
  const d = new Date(raw);
  return isNaN(d) ? 0 : d.getTime();
}

function normalizeTrack(name) {
  if (!name) return null;
  const n = name.trim().toLowerCase();
  return (n === 'unknown' || n === '') ? null : n;
}

// Merge-aware track identity. The leaderboard folds merged/variant/garbled
// session_name strings into one canonical circuit (e.g. the reversed
// "2 haodnanehS" and "Shenandoah S_u" both attribute to "Shenandoah"); prefer
// that canonical name over the raw per-file session_name label. Falls back to
// the raw name for sessions with no leaderboard entry (non-GPS / unmatched).
function trackDisplayName(s) {
  return (s && (s.canonical_track_name || s.session_name)) || null;
}

// Stable grouping/record key for a session's track. Uses the canonical track id
// when present so merged variants collapse into one group/record; otherwise
// falls back to the normalized raw name.
function trackKey(s) {
  if (s && s.canonical_track_id) return s.canonical_track_id;
  return normalizeTrack(s && s.session_name);
}

function formatGroupDateRange(group) {
  if (!group.startDateMs) return '';
  const start = new Date(group.startDateMs);
  const end   = new Date(group.endDateMs || group.startDateMs);
  const thisYear = new Date().getFullYear();
  const yearOpt  = start.getFullYear() !== thisYear ? { year: 'numeric' } : {};

  if (!group.endDateMs || group.startDateMs === group.endDateMs) {
    return start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...yearOpt });
  }
  const s = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const e = end.toLocaleDateString('en-US',   { month: 'short', day: 'numeric', ...yearOpt });
  return `${s}–${e}`;
}

// `newestFirst` orders the groups (and the sessions inside them) the way card
// view has always wanted them. The table hands over its own sort direction
// instead: with the Date column sorted ascending, newest-first groups would run
// counter to the order the server returned the rows in.
function computeGroups(sessions, groupLabels, standaloneIds, newestFirst = true) {
  const sorted = [...sessions].sort((a, b) => parseDateMs(a) - parseDateMs(b));
  const autoGroups = [];

  for (const s of sorted) {
    if (standaloneIds.has(s.session_id)) continue;

    const track  = trackKey(s);
    const dateMs = parseDateMs(s);

    if (!track || !dateMs) {
      let ug = autoGroups.find(g => g.isUngrouped);
      if (!ug) { ug = { isUngrouped: true, key: '__ungrouped__', sessions: [], startDateMs: 0, endDateMs: 0 }; autoGroups.push(ug); }
      ug.sessions.push(s);
      continue;
    }

    const existing = autoGroups.find(g =>
      !g.isUngrouped && !g.isStandalone &&
      g.track === track &&
      Math.abs(dateMs - g.startDateMs) <= 3 * 86400000
    );

    if (existing) {
      existing.sessions.push(s);
      existing.endDateMs = Math.max(existing.endDateMs, dateMs);
    } else {
      autoGroups.push({ key: `${track}__${dateMs}`, track, startDateMs: dateMs, endDateMs: dateMs, sessions: [s] });
    }
  }

  // Standalone sessions (removed from auto-groups)
  for (const s of sessions.filter(s => standaloneIds.has(s.session_id))) {
    autoGroups.push({
      key: `standalone__${s.session_id}`,
      track: trackKey(s),
      startDateMs: parseDateMs(s),
      endDateMs: parseDateMs(s),
      sessions: [s],
      isStandalone: true,
    });
  }

  // Sort groups, and the sessions within each group, in the requested direction
  const dir = newestFirst ? 1 : -1;
  autoGroups.sort((a, b) => dir * ((b.endDateMs || 0) - (a.endDateMs || 0)));
  for (const g of autoGroups) {
    if (!g.isUngrouped) g.sessions.sort((a, b) => dir * (parseDateMs(b) - parseDateMs(a)));
    g.label = groupLabels[g.key] || null;
  }

  return autoGroups;
}

// The table's render plan for grouped mode: header rows and session rows in the
// exact order they appear. Both the markup and the selection list are built from
// this one array, so "select all" and shift-click ranges can never disagree with
// what is on screen — a collapsed event's rows are absent from both, and hidden
// rows must not be bulk-deletable.
//
// Only *multi-session* events get a header. Card view gives even a lone session
// one, but a header per row would roughly double the table's height and cost it
// the density that is the reason to be in table view at all.
//
// Because a lone session has no header, it is only distinguishable from the
// event above it by what the event's rows carry: the rail/tint on every member
// row and `groupEnd` on the event's last visible row (the header itself when
// collapsed), which draws the closing edge. Without that edge a lone session
// sitting directly under an event reads as its last member.
function buildTableRows(groups, collapsedGroups) {
  const rows = [];
  let eventIndex = 0;
  for (const group of groups) {
    if (group.isUngrouped || group.sessions.length < 2) {
      for (const s of group.sessions) rows.push({ kind: 'session', session: s });
      continue;
    }
    // Adjacent events alternate rails so two back-to-back events at the same
    // track don't read as one long run of rows.
    const rail = eventIndex++ % 2 === 0 ? 'railA' : 'railB';
    const collapsed = collapsedGroups.has(group.key);
    rows.push({ kind: 'header', group, collapsed, rail, groupEnd: collapsed });
    if (!collapsed) {
      group.sessions.forEach((s, i) => {
        rows.push({ kind: 'session', session: s, rail, groupEnd: i === group.sessions.length - 1 });
      });
    }
  }
  return rows;
}

// Every listed session is selectable, shared ones included: a report over a
// friend's event is exactly the point of sharing it. Selection carries no
// permission — each bulk *action* decides what it can act on (delete only
// covers owned sessions, see ownedSelected; report and sync take the lot).
function groupSelectableIds(group) {
  return group.sessions.map(s => s.session_id);
}

function getGroupDefaultName(group) {
  const trackName = trackDisplayName(group.sessions[0]) || 'Track Event';
  const racers = [...new Set(
    group.sessions.map(s => s.racer).filter(r => r && r !== 'Unknown')
  )];
  return racers.length === 1 ? `${trackName} — ${racers[0]}` : trackName;
}

function groupStats(group) {
  const bestLap = group.sessions.reduce((best, s) => {
    if (s.best_lap_seconds != null && (best === null || s.best_lap_seconds < best.secs)) {
      return { secs: s.best_lap_seconds, time: s.best_lap_time };
    }
    return best;
  }, null);
  const totalLaps     = group.sessions.reduce((sum, s) => sum + (s.lap_count || 0), 0);
  const totalDuration = group.sessions.reduce((sum, s) => sum + (s.duration  || 0), 0);
  return { bestLap, totalLaps, totalDuration };
}

// ── Delete sessions dialog (event-group delete and multi-select bulk delete) ──
function DeleteSessionsDialog({ title, subtitle, sessions, onConfirm, onDismiss }) {
  const [deleting, setDeleting] = useState(false);
  const n = sessions.length;

  const handleConfirm = async () => {
    setDeleting(true);
    await onConfirm(sessions);
  };

  return (
    <div className={styles.duplicateOverlay}>
      <div className={styles.duplicateDialog}>
        <p className={styles.duplicateTitle}>{title}</p>
        <p className={styles.duplicateBody}>
          Permanently delete <strong>{n} session{n !== 1 ? 's' : ''}</strong>
          {subtitle && <> in <span className={styles.duplicateSessionName}>{subtitle}</span></>}?
          This cannot be undone.
        </p>
        {!deleting && (
          <div className={styles.duplicateActions}>
            <button className={styles.groupDeleteConfirmBtn} onClick={handleConfirm}>
              Delete {n} session{n !== 1 ? 's' : ''}
            </button>
            <button className={styles.duplicateBtnSecondary} onClick={onDismiss}>Cancel</button>
          </div>
        )}
        {deleting && (
          <p style={{ fontSize: '0.78rem', color: 'var(--muted)', margin: 0 }}>
            Deleting {n} session{n !== 1 ? 's' : ''}…
          </p>
        )}
      </div>
    </div>
  );
}

// ── Multi-event report confirmation ──────────────────────────────────────────
// A report pools every selected session into one set of numbers, so sessions
// from two different track days blend into one summary — and corner/sector
// sections render as unavailable if the events are at different circuits.
// List exactly which events are in play so a stray tick is easy to spot.
function MultiEventReportDialog({ events, sessionCount, onConfirm, onDismiss }) {
  const tracks = new Set(events.map(e => e.group.track).filter(Boolean));
  return (
    <div className={styles.duplicateOverlay}>
      <div className={styles.duplicateDialog}>
        <p className={styles.duplicateTitle}>Report across {events.length} events?</p>
        <p className={styles.duplicateBody}>
          The <strong>{sessionCount} selected session{sessionCount !== 1 ? 's' : ''}</strong> come
          from {events.length} separate events. One report pools them all: channel
          statistics and lap counts are combined across every session, and the
          per-session rows are listed together rather than event by event.
          {tracks.size > 1 && (
            <> They are also at <strong>{tracks.size} different tracks</strong>, so corner and
            sector sections will show as unavailable — those need one circuit.</>
          )}
        </p>
        <ul className={styles.multiEventList}>
          {events.map(({ group, hit }) => (
            <li key={group.key}>
              <span className={styles.duplicateSessionName}>{group.label || getGroupDefaultName(group)}</span>
              {' '}· {formatGroupDateRange(group)} · {hit} of {group.sessions.length} session{group.sessions.length !== 1 ? 's' : ''}
            </li>
          ))}
        </ul>
        <p className={styles.duplicateBody}>
          To report on one event, cancel and use its header checkbox to select just that event.
        </p>
        <div className={styles.duplicateActions}>
          <button className={styles.duplicateBtnPrimary} onClick={onConfirm}>Yes, build one report</button>
          <button className={styles.duplicateBtnSecondary} onClick={onDismiss}>No, go back</button>
        </div>
      </div>
    </div>
  );
}

// ── Group share modal ─────────────────────────────────────────────────────────
function GroupShareModal({ sessions, onClose, onAllShared, onAllUnshared }) {
  const [shareMode, setShareMode]     = useState('people');
  const [query, setQuery]             = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected]       = useState([]);
  const [shareAll, setShareAll]       = useState(false);
  const [permission, setPermission]   = useState('view');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [sharing, setSharing]         = useState(false);
  const [progress, setProgress]       = useState(null); // { done, total }
  const [errors, setErrors]           = useState([]);
  const [done, setDone]               = useState(false);

  const [groupOptions, setGroupOptions]       = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [groupPermission, setGroupPermission] = useState('view');
  const [groupSharing, setGroupSharing]       = useState(false);

  const [unshareConfirm, setUnshareConfirm]   = useState(false);
  const [unsharing, setUnsharing]             = useState(false);
  const [unshareProgress, setUnshareProgress] = useState(null);

  const searchRef   = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    searchRef.current?.focus();
    listGroups()
      .then(data => { setGroupOptions(data); if (data.length > 0) setSelectedGroupId(data[0].id); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (shareAll) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      if (query.length < 2) { setSuggestions([]); return; }
      try { setSuggestions(await searchUsers(query)); }
      catch { setSuggestions([]); }
    }, 200);
  }, [query, shareAll]);

  const selectUser = (user) => {
    setShareAll(false);
    setSelected(prev => prev.find(u => u.id === user.id) ? prev : [...prev, user]);
    setQuery(''); setSuggestions([]); setDropdownOpen(false);
    searchRef.current?.focus();
  };

  const handleShare = async (e) => {
    e.preventDefault();
    if (!shareAll && selected.length === 0) return;
    setSharing(true); setErrors([]);
    setProgress({ done: 0, total: sessions.length });
    const errs = [];
    for (let i = 0; i < sessions.length; i++) {
      const sid = sessions[i].session_id;
      try {
        if (shareAll) await shareSessionWithAll(sid, permission);
        else for (const u of selected) await shareSession(sid, u.email, permission);
      } catch (err) {
        errs.push(`${sessions[i].session_name || sid}: ${err.message}`);
      }
      setProgress({ done: i + 1, total: sessions.length });
    }
    setSharing(false); setProgress(null);
    if (errs.length === 0) { onAllShared?.(); setDone(true); setTimeout(onClose, 1500); }
    else setErrors(errs);
  };

  const handleGroupShare = async (e) => {
    e.preventDefault();
    if (!selectedGroupId) return;
    setGroupSharing(true); setErrors([]);
    setProgress({ done: 0, total: sessions.length });
    const errs = [];
    for (let i = 0; i < sessions.length; i++) {
      const sid = sessions[i].session_id;
      try { await shareSessionWithGroup(sid, selectedGroupId, groupPermission); }
      catch (err) { errs.push(`${sessions[i].session_name || sid}: ${err.message}`); }
      setProgress({ done: i + 1, total: sessions.length });
    }
    setGroupSharing(false); setProgress(null);
    if (errs.length === 0) { onAllShared?.(); setDone(true); setTimeout(onClose, 1500); }
    else setErrors(errs);
  };

  const handleUnshareAll = async () => {
    setUnsharing(true);
    setUnshareProgress({ done: 0, total: sessions.length });
    for (let i = 0; i < sessions.length; i++) {
      const sid = sessions[i].session_id;
      try { await removeGlobalShare(sid); } catch { /* no global share — fine */ }
      try {
        const shares = await listShares(sid);
        for (const sh of shares) {
          try { await removeShare(sid, sh.id); } catch {}
        }
      } catch {}
      try {
        const gShares = await listGroupShares(sid);
        for (const gs of gShares) {
          try { await removeGroupShare(sid, gs.group_id); } catch {}
        }
      } catch {}
      setUnshareProgress({ done: i + 1, total: sessions.length });
    }
    setUnsharing(false);
    setUnshareConfirm(false);
    onAllUnshared?.();
    setTimeout(onClose, 800);
  };

  const showDropdown = dropdownOpen && !shareAll && (suggestions.length > 0 || query.length < 2);
  const hasRecipient = shareAll || selected.length > 0;

  return (
    <div className={styles.duplicateOverlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={styles.groupShareDialog}>
        <div className={styles.groupShareHeader}>
          <div>
            <p className={styles.duplicateTitle}>Share event</p>
            <p className={styles.duplicateBody}>
              {sessions.length} session{sessions.length !== 1 ? 's' : ''} will be shared with the same access.
            </p>
          </div>
          <button className={styles.duplicateBtnDismiss} onClick={onClose} style={{ width: 'auto', padding: '0 4px' }}>×</button>
        </div>

        {done ? (
          <p className={styles.groupShareSuccess}>All sessions shared successfully.</p>
        ) : (
          <>
            <div className={styles.tabRow} style={{ marginBottom: '1rem' }}>
              {['people', 'group'].map(m => (
                <button key={m} className={`${styles.tabBtn} ${shareMode === m ? styles.tabActive : styles.tabInactive}`} onClick={() => setShareMode(m)}>
                  {m.charAt(0).toUpperCase() + m.slice(1)}
                </button>
              ))}
            </div>

            {shareMode === 'people' && (
              <form onSubmit={handleShare}>
                <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
                  <div className={styles.groupShareChipInput} onClick={() => !shareAll && searchRef.current?.focus()}>
                    {shareAll ? (
                      <span className={styles.groupShareChip}>
                        All users <button type="button" onClick={() => setShareAll(false)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: '0 0 0 4px' }}>×</button>
                      </span>
                    ) : (
                      <>
                        {selected.map(u => (
                          <span key={u.id} className={styles.groupShareChip}>
                            {u.display_name || u.email}
                            <button type="button" onClick={() => setSelected(p => p.filter(x => x.id !== u.id))} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: '0 0 0 4px' }}>×</button>
                          </span>
                        ))}
                        <input
                          ref={searchRef}
                          className={styles.groupShareSearchInput}
                          type="text"
                          placeholder={selected.length === 0 ? 'Search by name or email…' : ''}
                          value={query}
                          onChange={e => setQuery(e.target.value)}
                          onFocus={() => setDropdownOpen(true)}
                          onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
                          autoComplete="off"
                        />
                      </>
                    )}
                  </div>
                  {showDropdown && (
                    <ul className={styles.groupShareDropdown}>
                      <li className={styles.groupShareDropdownItem} onMouseDown={() => { setShareAll(true); setQuery(''); setDropdownOpen(false); }}>
                        <span>All users</span><span style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>Share with everyone</span>
                      </li>
                      {suggestions.map(u => (
                        <li key={u.id} className={styles.groupShareDropdownItem} onMouseDown={() => selectUser(u)}>
                          <span>{u.display_name || u.email}</span>
                          {u.display_name && <span style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>{u.email}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className={styles.groupShareRow}>
                  <select className={styles.groupShareSelect} value={permission} onChange={e => setPermission(e.target.value)}>
                    <option value="view">View</option>
                    <option value="comment">Comment</option>
                  </select>
                  <button className={styles.duplicateBtnPrimary} type="submit" disabled={sharing || !hasRecipient} style={{ flex: 'none', padding: '8px 16px' }}>
                    {sharing && progress ? `Sharing ${progress.done}/${progress.total}…` : `Share ${sessions.length} sessions`}
                  </button>
                </div>
              </form>
            )}

            {shareMode === 'group' && (
              <form onSubmit={handleGroupShare}>
                <div className={styles.groupShareRow}>
                  <select className={`${styles.groupShareSelect} ${styles.groupShareSelectWide}`} value={selectedGroupId} onChange={e => setSelectedGroupId(e.target.value)} disabled={groupOptions.length === 0}>
                    {groupOptions.length === 0 ? <option value="">No groups yet</option> : groupOptions.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                  <select className={styles.groupShareSelect} value={groupPermission} onChange={e => setGroupPermission(e.target.value)}>
                    <option value="view">View</option>
                    <option value="comment">Comment</option>
                  </select>
                  <button className={styles.duplicateBtnPrimary} type="submit" disabled={groupSharing || !selectedGroupId || groupOptions.length === 0} style={{ flex: 'none', padding: '8px 16px' }}>
                    {groupSharing && progress ? `Sharing ${progress.done}/${progress.total}…` : `Share ${sessions.length} sessions`}
                  </button>
                </div>
              </form>
            )}

            {errors.length > 0 && (
              <div style={{ marginTop: '0.75rem' }}>
                {errors.map((e, i) => <p key={i} style={{ color: 'var(--accent)', fontSize: '0.78rem', margin: '2px 0' }}>⚠ {e}</p>)}
              </div>
            )}

            <div className={styles.groupUnshareSection}>
              {!unshareConfirm ? (
                <button className={styles.groupUnshareBtn} onClick={() => setUnshareConfirm(true)}>
                  Unshare all sessions
                </button>
              ) : (
                <div className={styles.groupUnshareConfirm}>
                  <p className={styles.groupUnsharePrompt}>
                    Remove access from all recipients for all {sessions.length} sessions?
                  </p>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className={styles.groupUnshareConfirmBtn}
                      onClick={handleUnshareAll}
                      disabled={unsharing}
                    >
                      {unsharing && unshareProgress
                        ? `Removing ${unshareProgress.done}/${unshareProgress.total}…`
                        : 'Yes, unshare all'}
                    </button>
                    <button className={styles.editCancelBtn} onClick={() => setUnshareConfirm(false)} disabled={unsharing}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── 9b. VTR render queue panel ────────────────────────────────────────────────
const STATUS_COLORS = {
  queued:    'var(--muted)',
  encoding:  '#f5a623',
  completed: '#44cc88',
  failed:    'var(--accent)',
};

function VtrQueueJob({ job }) {
  const color = STATUS_COLORS[job.status] || 'var(--muted)';
  const createdAt = job.created_at ? new Date(job.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <div className={styles.vtrJob}>
      <div className={styles.vtrJobTop}>
        <span className={styles.vtrJobId} title={job.job_id}>{job.job_id.slice(0, 8)}…</span>
        <span className={styles.vtrJobStatus} style={{ color }}>{job.status}</span>
        {createdAt && <span className={styles.vtrJobTime}>{createdAt}</span>}
      </div>
      {job.stage_message && (
        <span className={styles.vtrJobMsg}>{job.stage_message}</span>
      )}
      {job.status === 'encoding' && job.progress_pct != null && (
        <div className={styles.vtrBar}>
          <div className={styles.vtrBarFill} style={{ width: `${job.progress_pct}%` }} />
        </div>
      )}
    </div>
  );
}

function VtrRenderQueue() {
  const [jobs, setJobs]       = useState([]);
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const data = await getVtrQueue();
        if (!cancelled) { setJobs(data); setLoading(false); }
      } catch {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    const id = setInterval(fetch, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const active = jobs.filter(j => j.status !== 'completed' && j.status !== 'failed');
  const hasJobs = jobs.length > 0;

  return (
    <div className={styles.vtrQueueWrap}>
      <button
        className={`${styles.vtrQueueToggle} ${active.length > 0 ? styles.vtrQueueToggleActive : ''}`}
        onClick={() => setOpen(o => !o)}
        title="View render job queue"
      >
        {loading ? (
          <span className={styles.vtrQueueDot} style={{ background: 'var(--muted)' }} />
        ) : (
          <span
            className={`${styles.vtrQueueDot} ${active.length > 0 ? styles.vtrQueueDotPulse : ''}`}
            style={{ background: active.length > 0 ? '#f5a623' : hasJobs ? '#44cc88' : 'var(--border-strong)' }}
          />
        )}
        Render Queue
        {active.length > 0 && <span className={styles.vtrQueueBadge}>{active.length}</span>}
        <span className={styles.vtrQueueChevron}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className={styles.vtrQueuePanel}>
          <div className={styles.vtrQueueHeader}>
            <span className={styles.vtrQueueTitle}>VTR Job Queue</span>
            <span className={styles.vtrQueueCount}>{jobs.length} job{jobs.length !== 1 ? 's' : ''}</span>
          </div>
          {jobs.length === 0 ? (
            <p className={styles.vtrQueueEmpty}>No jobs in queue — system is idle.</p>
          ) : (
            <div className={styles.vtrJobList}>
              {jobs.map(j => <VtrQueueJob key={j.job_id} job={j} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Admin send-link modal ─────────────────────────────────────────────────────
function AdminSendLinkModal({ session, adminDisplayName, onClose, onSent }) {
  const defaultSubject = `Your track session from ${session.session_name || 'the track'} is ready`;
  const buildDefaultBody = (shareUrl) => {
    const date = session.date || '';
    const track = session.session_name || 'the track';
    return `Hi,

Your driving data from ${track}${date ? ` on ${date}` : ''} has been processed and is ready for review.

Click the link below to explore your lap times, speed traces, and coaching notes — no account required:

${shareUrl}

If you have any questions or want to dig deeper, feel free to reply to this email.

${adminDisplayName || 'TrackAnalysis'}`;
  };

  const [recipientEmail, setRecipientEmail] = useState(session.recipient_email || '');
  const [subject, setSubject]               = useState(defaultSubject);
  const [body, setBody]                     = useState('');
  const [shareUrl, setShareUrl]             = useState('');
  const [loadingLink, setLoadingLink]       = useState(true);
  const [sending, setSending]               = useState(false);
  const [sent, setSent]                     = useState(false);
  const [error, setError]                   = useState('');

  useEffect(() => {
    createPublicLink(session.session_id)
      .then(link => {
        const url = `${window.location.origin}/public/${link.token}`;
        setShareUrl(url);
        setBody(buildDefaultBody(url));
      })
      .catch(() => setError('Could not create share link.'))
      .finally(() => setLoadingLink(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = async (e) => {
    e.preventDefault();
    if (!recipientEmail || !subject || !body) return;
    setSending(true);
    setError('');
    try {
      await sendSessionInvite(session.session_id, recipientEmail, subject, body);
      setSent(true);
      setTimeout(() => { onSent?.(); onClose(); }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to send email.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={styles.duplicateOverlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={styles.groupShareDialog} style={{ maxWidth: 560, width: '95vw' }}>
        <div className={styles.groupShareHeader}>
          <div>
            <p className={styles.duplicateTitle}>Send Session Link</p>
            <p className={styles.duplicateBody} style={{ margin: 0 }}>
              {session.session_name || 'Session'}{session.date ? ` · ${session.date}` : ''}
            </p>
          </div>
          <button className={styles.duplicateBtnDismiss} onClick={onClose} style={{ width: 'auto', padding: '0 4px' }}>×</button>
        </div>

        {sent ? (
          <p style={{ color: '#44cc88', fontSize: '0.9rem', margin: '1rem 0 0' }}>Email sent successfully.</p>
        ) : loadingLink ? (
          <p style={{ color: 'var(--muted)', fontSize: '0.85rem', margin: '1rem 0 0' }}>Generating share link…</p>
        ) : (
          <form onSubmit={handleSend} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '1rem' }}>
            <label style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>
              To
              <input
                className={styles.editInput}
                type="email"
                required
                value={recipientEmail}
                onChange={e => setRecipientEmail(e.target.value)}
                placeholder="recipient@example.com"
                style={{ display: 'block', width: '100%', marginTop: 3 }}
              />
            </label>
            <label style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>
              Subject
              <input
                className={styles.editInput}
                type="text"
                required
                value={subject}
                onChange={e => setSubject(e.target.value)}
                style={{ display: 'block', width: '100%', marginTop: 3 }}
              />
            </label>
            <label style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>
              Body
              <textarea
                className={styles.editInput}
                required
                value={body}
                onChange={e => setBody(e.target.value)}
                rows={10}
                style={{ display: 'block', width: '100%', marginTop: 3, resize: 'vertical', fontFamily: 'monospace', fontSize: '0.78rem', lineHeight: 1.5 }}
              />
            </label>
            {shareUrl && (
              <p style={{ fontSize: '0.72rem', color: 'var(--muted)', margin: 0, wordBreak: 'break-all' }}>
                Share URL: {shareUrl}
              </p>
            )}
            {error && <p style={{ color: 'var(--accent)', fontSize: '0.8rem', margin: 0 }}>⚠ {error}</p>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <button type="button" className={styles.editCancelBtn} onClick={onClose} disabled={sending}>Cancel</button>
              <button type="submit" className={styles.duplicateBtnPrimary} disabled={sending || !recipientEmail}>
                {sending ? 'Sending…' : 'Send Email'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}


// ── Main component ────────────────────────────────────────────────────────────
export default function UploadPanel({
  onUpload, onUploadVideo, onUploadXrk, onUploadMotec, onUploadSmartycam3, onLoadSession,
  loading, error, uploadProgress, duplicateInfo, onForceUpload, onClearDuplicate,
  unitPrefs, currentUser, uploadSignal, adminModeActive, sessionSharing = true,
  desktopSync = false, onOpenServerSync, onOpenReport,
  multiSessionNotice, onClearMultiSessionNotice,
}) {
  const inputRef       = useRef(null);
  const folderInputRef = useRef(null);
  const queueRef     = useRef([]);
  const processingRef = useRef(false);

  // The folder picker only offers directory selection if the input carries the
  // `webkitdirectory` attribute. Setting it declaratively in JSX is unreliable
  // (React's attribute handling can drop it, leaving a plain file picker), so we
  // set it imperatively on the mounted DOM node. `directory` is the older Firefox
  // spelling; both are harmless to set together.
  useEffect(() => {
    const el = folderInputRef.current;
    if (!el) return;
    el.setAttribute('webkitdirectory', '');
    el.setAttribute('directory', '');
  }, []);

  const PAGE_SIZE = 50;
  const EMPTY_FACETS = { tracks: [], cars: [], drivers: [], temp_min: null, temp_max: null, date_min: null, date_max: null, has_wet: false, has_dry: false };
  const EMPTY_STATS  = { laps: 0, sessions: 0, tracks: 0, hours: 0, track_bests: {} };
  const tempUnit = unitPrefs?.temp === 'C' ? 'C' : 'F';
  const restoredFilters = useMemo(() => readSessionFilters(currentUser?.id), [currentUser?.id]);

  const [dragging, setDragging]           = useState(false);
  const [scanningFolder, setScanningFolder] = useState(false);
  const [uploadFilename, setUploadFilename] = useState('');
  const [sessions, setSessions]           = useState([]);   // accumulated pages under the current filters/sort
  const [cursor, setCursor]               = useState(null);
  const [hasMore, setHasMore]             = useState(false);
  const [loadingMore, setLoadingMore]     = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true); // first-ever fetch only
  const [hasAnySessions, setHasAnySessions] = useState(null);   // from a dedicated unfiltered probe, never a filtered fetch
  const [facets, setFacets]               = useState(EMPTY_FACETS);
  const [facetsReady, setFacetsReady]     = useState(false);
  const [stats, setStats]                 = useState(EMPTY_STATS);
  const [deletingId, setDeletingId]       = useState(null);
  const [selectedIds, setSelectedIds]     = useState(new Set()); // multi-select for bulk delete / report
  const [confirmMultiEventReport, setConfirmMultiEventReport] = useState(null); // report params awaiting confirmation
  const lastClickedIdRef = useRef(null); // anchor session_id for shift-click range selection
  // Native 'change' events don't carry modifier keys, so onClick captures shiftKey
  // here (without preventing default — letting the native/controlled checkbox
  // toggle proceed normally) for the onChange handler to read.
  const shiftKeyRef = useRef(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(null); // session[] | null
  const [downloadingId, setDownloadingId] = useState(null);
  const [filterText, setFilterText]       = useState(restoredFilters.filterText || '');
  const [filterTrack,  setFilterTrack]   = useState(restoredFilters.filterTrack || '');
  const [filterCar,    setFilterCar]     = useState(restoredFilters.filterCar || '');
  const [filterDriver, setFilterDriver]  = useState(restoredFilters.filterDriver || '');
  const [tempRange,  setTempRange]  = useState(() =>
    convertTempRange(restoredFilters.tempRange, restoredFilters.tempUnit, tempUnit) || null
  ); // [lo,hi] in display unit; null = untouched
  const [dateField,  setDateField]  = useState(restoredFilters.dateField || 'session'); // 'session' | 'uploaded' — which date the slider filters
  const [conditions, setConditions] = useState(restoredFilters.conditions || 'all');  // 'all' | 'dry' | 'wet'
  const [sharingSession, setSharingSession] = useState(null);
  const [renderSession, setRenderSession]   = useState(null);
  const [renderInitialStep, setRenderInitialStep] = useState(1);
  const [renderingIds, setRenderingIds]     = useState(new Set());
  const [syncQueue, setSyncQueue]           = useState(null);
  const [syncDialog, setSyncDialog]         = useState(null);
  const [syncPlan, setSyncPlan]             = useState(null);
  const [syncPlanning, setSyncPlanning]     = useState(false);
  const [syncError, setSyncError]           = useState('');
  const [syncPollError, setSyncPollError]   = useState('');

  useEffect(() => {
    if (!desktopSync) return undefined;
    let disposed = false;
    let timer;
    const poll = async () => {
      try {
        const next = await getDesktopSyncQueue();
        if (!disposed) {
          setSyncQueue(next);
          setSyncPollError('');
        }
      } catch (err) {
        if (!disposed) setSyncPollError(err.message);
      } finally {
        if (!disposed) timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { disposed = true; clearTimeout(timer); };
  }, [desktopSync]);

  const syncItemsBySession = useMemo(() => {
    const latest = new Map();
    for (const item of syncQueue?.items || []) latest.set(item.session_id, item);
    return latest;
  }, [syncQueue?.items]);

  const openSyncPlan = useCallback(async (sessionIds, label) => {
    setSyncDialog({ sessionIds, label });
    setSyncPlan(null);
    setSyncPlanning(true);
    setSyncError('');
    try {
      setSyncPlan(await planDesktopSyncQueue(sessionIds));
    } catch (err) {
      setSyncError(err.message);
    } finally {
      setSyncPlanning(false);
    }
  }, []);

  const startPlannedSync = useCallback(async (syncMode, restoreHosted = false) => {
    if (!syncDialog) return;
    setSyncError('');
    setSyncPlanning(true);
    try {
      const next = await startDesktopSyncQueue(
        syncDialog.sessionIds,
        syncMode,
        restoreHosted,
      );
      setSyncQueue(next);
      setSyncDialog(null);
      setSyncPlan(null);
      if (syncDialog.sessionIds) setSelectedIds(new Set());
    } catch (err) {
      setSyncError(err.message);
    } finally {
      setSyncPlanning(false);
    }
  }, [syncDialog]);

  const updateSyncQueue = useCallback(async (action) => {
    setSyncError('');
    try {
      setSyncQueue(await action());
    } catch (err) {
      setSyncError(err.message);
    }
  }, []);

  // `s` is normally a session-index row and can be only { session_id } when the
  // video matcher jumps straight into the editor. Hydrate it with the complete
  // session so editor-only fields such as channels and meta.duration survive.
  const openRenderModal = useCallback(async (s, step) => {
    setRenderInitialStep(step);
    try {
      const full = await loadSession(s.session_id);
      setRenderSession(mergeRenderSession(s, full));
    } catch {
      setRenderSession(s);
    }
  }, []);
  const [activeTab, setActiveTab]         = useState(
    sessionSharing ? (restoredFilters.activeTab || 'all') : 'all'
  );
  const [editingId, setEditingId]         = useState(null);
  const [editDraft, setEditDraft]         = useState({ session_name: '', vehicle: '', racer: '' });
  const [savingEdit, setSavingEdit]       = useState(false);

  // Sessions the current user has shared out.
  // Server populates has_shares on each owned session; local set handles optimistic additions.
  const [localSharedIds, setLocalSharedIds] = useState(new Set());
  const sharedByMeIds = new Set([
    ...sessions.filter(s => s.has_shares).map(s => s.session_id),
    ...localSharedIds,
  ]);

  const [sharingGroup, setSharingGroup]           = useState(null); // group object | null
  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState(null); // group object | null

  // Batch queue (display copy of queueRef)
  const [queueDisplay, setQueueDisplay]   = useState([]);
  const [batchWarning, setBatchWarning]   = useState('');
  const [pendingBatch, setPendingBatch]   = useState(null); // File[] staged for the confirm modal
  const [ecuMatchFiles, setEcuMatchFiles] = useState(null); // File[] for the ECU-match flow (null = closed)
  const [videoMatchFiles, setVideoMatchFiles] = useState(null); // File[] for the video-match flow (null = closed)

  // Group overrides — persisted in localStorage
  const [groupLabels, setGroupLabels] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ta_groupLabels') || '{}'); } catch { return {}; }
  });
  const [standaloneIds, setStandaloneIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('ta_standaloneIds') || '[]')); } catch { return new Set(); }
  });
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('ta_collapsedGroups') || '[]')); } catch { return new Set(); }
  });

  const [viewMode, setViewMode] = useState(() => localStorage.getItem('ta_viewMode') || 'cards');
  const [tableGrouped, setTableGrouped] = useState(() => localStorage.getItem('ta_tableGrouped') !== '0');
  // Single active sort column — the backend only supports one at a time (see
  // SORT_COLUMNS in api/sessions.py). Multi-level client sort is gone along
  // with client-side sorting itself; the server returns pages pre-sorted.
  const [sort, setSort] = useState(restoredFilters.sort || { col: 'date', dir: 'desc' });

  // Mirrors the table view's own delete-button gate (card view's is stricter — see renderCard).
  const isAdmin = currentUser?.is_admin && adminModeActive;

  const [adminRecipientEmail, setAdminRecipientEmail] = useState('');
  const [sendLinkSession, setSendLinkSession]         = useState(null);
  const [reassignSession, setReassignSession]         = useState(null); // admin: session being reassigned
  const [indexingId, setIndexingId]                   = useState(null); // admin: session being indexed for search

  // Index an already-rendered session for video search. No re-render: this decodes
  // the existing output, so it takes a minute or two rather than half an hour.
  const handleIndexVideo = useCallback(async (session) => {
    setIndexingId(session.session_id);
    try {
      const result = await indexSessionVideo(session.session_id, true);
      window.alert(result.message || 'Indexing queued.');
    } catch (e) {
      window.alert(`Could not index this session: ${e.message}`);
    } finally {
      setIndexingId(null);
    }
  }, []);
  const [reassignTracks, setReassignTracks]           = useState([]);

  // Unscoped track catalog (not getLeaderboardTracks, which only lists tracks
  // where THIS user has visible entries) — an admin must be able to move a
  // session to any track definition, including ones with no visible entries.
  useEffect(() => {
    if (!currentUser?.is_admin) return;
    getTracks().then(setReassignTracks).catch(() => {});
  }, [currentUser?.is_admin]);

  // With admin mode off, an admin acts as a normal user: hide sessions that are
  // only visible via the admin override, but KEEP ones genuinely shared with them
  // (shared_with_me) so their Shared tab still populates. This stays a light
  // client-side post-filter over whatever page(s) are already loaded.
  const effectiveSessions = useMemo(() =>
    (currentUser?.is_admin && !adminModeActive)
      ? sessions.filter(s => !s.is_admin_view || s.shared_with_me)
      : sessions,
  [sessions, currentUser, adminModeActive]);

  const groups = useMemo(
    () => computeGroups(effectiveSessions, groupLabels, standaloneIds),
    [effectiveSessions, groupLabels, standaloneIds]
  );

  // Events only line up with the rows when the table is in date order; under any
  // other sort an event's sessions are scattered and banding them would be
  // noise. So grouping suspends itself rather than overriding the sort the user
  // asked for — the toggle goes muted and says why (see the toolbar below).
  const tableGroupingActive = tableGrouped && sort.col === 'date';
  const tableGroups = useMemo(
    () => (sort.dir === 'desc'
      ? groups
      : computeGroups(effectiveSessions, groupLabels, standaloneIds, false)),
    [groups, sort.dir, effectiveSessions, groupLabels, standaloneIds]
  );

  // Flattened, collapse-aware order of card-view's session_ids — used to
  // compute shift-click ranges (must skip rows hidden behind a collapsed group).
  const cardVisibleIds = useMemo(() => {
    const ids = [];
    for (const group of groups) {
      if (!group.isUngrouped && collapsedGroups.has(group.key)) continue;
      for (const s of group.sessions) ids.push(s.session_id);
    }
    return ids;
  }, [groups, collapsedGroups]);

  // How the selection relates to events, for the bulk toolbar: how many events
  // it touches, and — when it is exactly one whole event — that event's name,
  // which becomes the report's default title. The ungrouped bucket isn't an
  // event, so it is never counted.
  const { selectedEvents, selectedEventCount, selectionEventTitle } = useMemo(() => {
    const events = [];
    let title = null;
    for (const group of groups) {
      if (group.isUngrouped) continue;
      const ids = groupSelectableIds(group);
      const hit = ids.filter(id => selectedIds.has(id)).length;
      if (hit === 0) continue;
      events.push({ group, hit });
      title = hit === ids.length && ids.length === selectedIds.size
        ? (group.label || getGroupDefaultName(group))
        : null;
    }
    return {
      selectedEvents: events,
      selectedEventCount: events.length,
      selectionEventTitle: events.length === 1 ? title : null,
    };
  }, [groups, selectedIds]);

  // ── Weather / date unit conversion ────────────────────────────────────────
  // Telemetry weather is stored in °C; convert to the user's display unit.
  const cToDisp = (c) => tempUnit === 'F' ? c * 9 / 5 + 32 : c;
  const dispToC = (v) => tempUnit === 'F' ? (v - 32) * 5 / 9 : v;

  // Track/car/driver dropdown options + temp/date bounds now come from the
  // server (GET /sessions/facets), scoped to the current filters/tab — see
  // the fetch effect below. Whole-degree bounds, matching the old client math.
  const tempBounds = useMemo(() => {
    if (facets.temp_min == null || facets.temp_max == null) return null;
    const min = Math.floor(cToDisp(facets.temp_min));
    const max = Math.ceil(cToDisp(facets.temp_max));
    return max > min ? { min, max } : null;
  }, [facets.temp_min, facets.temp_max, tempUnit]);

  const dateBounds = useMemo(() => {
    if (!facets.date_min || !facets.date_max) return null;
    const minRaw = new Date(facets.date_min).getTime();
    const maxRaw = new Date(facets.date_max).getTime();
    if (isNaN(minRaw) || isNaN(maxRaw)) return null;
    const min = Math.floor(minRaw / DAY_MS) * DAY_MS;
    const max = Math.ceil(maxRaw / DAY_MS) * DAY_MS;
    return max > min ? { min, max } : null;
  }, [facets.date_min, facets.date_max]);

  const condAvail = useMemo(() => ({ wet: facets.has_wet, dry: facets.has_dry }), [facets.has_wet, facets.has_dry]);

  // Clear stale selections when the server-reported options no longer include them.
  useEffect(() => { if (facetsReady && filterTrack  && !facets.tracks.includes(filterTrack))   setFilterTrack('');  }, [facetsReady, facets.tracks]);
  useEffect(() => { if (facetsReady && filterCar    && !facets.cars.includes(filterCar))       setFilterCar('');    }, [facetsReady, facets.cars]);
  useEffect(() => { if (facetsReady && filterDriver && !facets.drivers.includes(filterDriver)) setFilterDriver(''); }, [facetsReady, facets.drivers]);

  // Switching the date source resets the range so a narrowed window doesn't
  // carry over to an unrelated set of dates (the bounds effect re-fits to full).
  const {
    range: dateRange, setRange: setDateRange, applyPreset: applyDatePreset,
    reset: resetDateRange, sliderMin: dateSliderMin, sliderMax: dateSliderMax,
    activePreset: activeDatePreset,
  } = useDateRangePresets(dateBounds);
  useEffect(() => { resetDateRange(); }, [dateField]); // eslint-disable-line react-hooks/exhaustive-deps

  // Date bounds arrive asynchronously from the facets endpoint. Re-apply the
  // saved selection once those bounds exist; using applyPreset also restores
  // the preset highlight and its zoomed slider domain.
  const pendingDateRestoreRef = useRef(restoredFilters.dateRange
    ? { range: restoredFilters.dateRange, preset: restoredFilters.datePreset }
    : null);
  const [dateRestoreComplete, setDateRestoreComplete] = useState(!pendingDateRestoreRef.current);
  useEffect(() => {
    if (!dateBounds || !pendingDateRestoreRef.current) return;
    const saved = pendingDateRestoreRef.current;
    pendingDateRestoreRef.current = null;
    if (saved.preset) applyDatePreset(saved.preset);
    else setDateRange(saved.range);
    setDateRestoreComplete(true);
  }, [dateBounds, applyDatePreset, setDateRange]);

  const rememberSessionFilters = useCallback(() => {
    writeSessionFilters(currentUser?.id, {
      filterText, filterTrack, filterCar, filterDriver,
      tempRange, tempUnit,
      dateField, dateRange, datePreset: activeDatePreset,
      conditions, activeTab, sort,
    });
  }, [currentUser?.id, filterText, filterTrack, filterCar, filterDriver,
      tempRange, tempUnit, dateField, dateRange, activeDatePreset,
      conditions, activeTab, sort]);

  // Save continuously, and once more synchronously when a row is opened so a
  // very quick filter-then-click interaction cannot outrun the effect flush.
  useEffect(() => {
    if (facetsReady && dateRestoreComplete) rememberSessionFilters();
  }, [facetsReady, dateRestoreComplete, rememberSessionFilters]);
  const handleLoadSession = useCallback((...args) => {
    rememberSessionFilters();
    onLoadSession?.(...args);
  }, [rememberSessionFilters, onLoadSession]);

  // Initialise / clamp the temp slider range to its bounds as the underlying
  // data changes (bounds are scoped by track/car/driver, so picking or
  // clearing any of those shifts them). Returns the SAME array reference when
  // nothing actually changed, so this never creates a spurious new tempRange
  // identity that would re-trigger the fetch effect below in a loop.
  //
  // Critically, if the range was sitting at the *previous* bounds' full extent
  // (i.e. the user never deliberately narrowed it — it was just riding along
  // with whatever the bounds were), a bounds change must snap it to the NEW
  // full extent, not clamp it into place. Otherwise clearing a track/car/driver
  // filter that had auto-narrowed the bounds leaves the slider stuck at that
  // stale sub-range, silently applying a temp filter the user never set
  // (exactly what clamping-without-this-check does). (dateRange gets the same
  // treatment inside useDateRangePresets.)
  const prevTempBoundsRef = useRef(null);
  useEffect(() => {
    const prev = prevTempBoundsRef.current;
    prevTempBoundsRef.current = tempBounds;
    if (!tempBounds) { setTempRange(r => r === null ? r : null); return; }
    setTempRange(r => {
      if (!r) return [tempBounds.min, tempBounds.max];
      const wasFullRange = prev && r[0] === prev.min && r[1] === prev.max;
      const target = wasFullRange
        ? [tempBounds.min, tempBounds.max]
        : [Math.max(tempBounds.min, Math.min(r[0], tempBounds.max)),
           Math.min(tempBounds.max, Math.max(r[1], tempBounds.min))];
      return (target[0] === r[0] && target[1] === r[1]) ? r : target;
    });
  }, [tempBounds]);
  useEffect(() => {
    if ((conditions === 'wet' && !condAvail.wet) || (conditions === 'dry' && !condAvail.dry)) setConditions('all');
  }, [condAvail, conditions]);

  const switchView = (mode) => {
    setViewMode(mode);
    localStorage.setItem('ta_viewMode', mode);
  };

  // Every dimension the list can be narrowed by. The ownership tab counts:
  // "Shared with me" hides your own sessions just as effectively as a track
  // filter does, and it's persisted across visits the same way.
  const tempNarrowed = !!(tempBounds && tempRange && (tempRange[0] > tempBounds.min || tempRange[1] < tempBounds.max));
  const dateNarrowed = !!(dateBounds && dateRange && (dateRange[0] > dateBounds.min || dateRange[1] < dateBounds.max));
  const filtersActive = !!(filterText || activeTab !== 'all' || filterTrack || filterCar || filterDriver
                           || conditions !== 'all' || tempNarrowed || dateNarrowed);
  // "Clear all filters" is the one control that promises a default list, and
  // the sort is part of that: a non-date sort silently pauses event grouping
  // in table view (see tableGroupingActive), which reads as the groups having
  // vanished. Put the sort back too, so one click restores everything.
  const clearAllFilters = useCallback(() => {
    setFilterText(''); setFilterTrack(''); setFilterCar(''); setFilterDriver('');
    setConditions('all');
    setActiveTab('all');
    setTempRange(tempBounds ? [tempBounds.min, tempBounds.max] : null);
    resetDateRange();
    setSort({ col: 'date', dir: 'desc' });
  }, [tempBounds, resetDateRange]);

  const handleSort = (col) => {
    setSort(prev => nextSessionSort(prev, col));
  };

  // ── Server-side fetch: filters/sort/tab change → reset to page 1 (debounced,
  // since temp/date sliders fire onChange continuously while dragging) ───────
  const buildBaseParams = useCallback(() => {
    const params = { tab: activeTab, sort: sort.col, sort_dir: sort.dir, page_size: PAGE_SIZE };
    const q = filterText.trim();
    if (q) params.q = q;
    if (filterTrack)  params.track  = filterTrack;
    if (filterCar)    params.car    = filterCar;
    if (filterDriver) params.driver = filterDriver;
    if (conditions !== 'all') params.condition = conditions;
    if (dateField !== 'session') params.date_field = dateField;
    if (tempBounds && tempRange && (tempRange[0] > tempBounds.min || tempRange[1] < tempBounds.max)) {
      params.temp_min = dispToC(tempRange[0]);
      params.temp_max = dispToC(tempRange[1]);
    }
    if (dateBounds && dateRange && (dateRange[0] > dateBounds.min || dateRange[1] < dateBounds.max)) {
      params.date_from = new Date(dateRange[0]).toISOString();
      params.date_to = new Date(dateRange[1]).toISOString();
    }
    return params;
  }, [activeTab, sort, filterText, filterTrack, filterCar, filterDriver, conditions, dateField, tempBounds, tempRange, dateBounds, dateRange]);

  // "Does this account have any sessions at all?" gates the entire Recent
  // Sessions block — filter controls included — so it must never be answered by
  // a filtered fetch. It used to be frozen from whichever list response landed
  // first, and that is reliably the *filtered* one: the unfiltered fetch fired
  // on mount is superseded (and discarded) as soon as the restored date range
  // arrives from the facets round-trip. A restored filter matching nothing then
  // hid the very controls needed to clear it, and since the filters live in
  // sessionStorage the lockout survived reloads — which is exactly what a
  // collapsed date range (date_from === date_to) did to a real account.
  //
  // So ask the question on its own terms, once, with no filters, and fail open.
  useEffect(() => {
    let cancelled = false;
    listSessions({ tab: 'all', page_size: 1 })
      .then(d => { if (!cancelled) setHasAnySessions(d.sessions.length > 0 || !!d.next_cursor); })
      .catch(() => { if (!cancelled) setHasAnySessions(true); });
    return () => { cancelled = true; };
  }, [currentUser?.id]);

  const fetchSeqRef = useRef(0);
  const fetchDebounceRef = useRef(null);

  useEffect(() => {
    const seq = ++fetchSeqRef.current;
    clearTimeout(fetchDebounceRef.current);
    fetchDebounceRef.current = setTimeout(() => {
      const params = buildBaseParams();
      listSessions(params)
        .then(data => {
          if (seq !== fetchSeqRef.current) return; // superseded by a newer filter/sort change
          setSessions(data.sessions);
          setCursor(data.next_cursor);
          setHasMore(!!data.next_cursor);
          // Monotonic only: rows on screen prove the account has something (so
          // a first upload reveals the list without waiting for a reload), but
          // an empty *filtered* page proves nothing and must never hide it.
          if (data.sessions.length > 0) setHasAnySessions(true);
        })
        .catch(() => {
          if (seq !== fetchSeqRef.current) return;
          setSessions([]); setCursor(null); setHasMore(false);
        })
        .finally(() => { if (seq === fetchSeqRef.current) setLoadingSessions(false); });

      getSessionFacets(params).then(f => {
        if (seq === fetchSeqRef.current) {
          setFacets(f);
          setFacetsReady(true);
        }
      }).catch(() => {});
    }, 250);
    return () => clearTimeout(fetchDebounceRef.current);
  }, [buildBaseParams]);

  // Any filter/sort/tab change reloads page 1 above — a stale selection
  // referencing sessions no longer in view would be confusing, so drop it too.
  useEffect(() => { setSelectedIds(new Set()); lastClickedIdRef.current = null; }, [buildBaseParams]);

  const refreshSessions = useCallback(() => {
    const params = buildBaseParams();
    return listSessions(params).then(data => {
      setSessions(data.sessions);
      setCursor(data.next_cursor);
      setHasMore(!!data.next_cursor);
      return data;
    });
  }, [buildBaseParams]);

  // The progress modal owns polling while it is open. Once it is closed, keep
  // reconciling active jobs here so the optimistic renderingIds flag cannot
  // leave the card/table badge stuck after a background render completes.
  // Include server-reported jobs as well so polling resumes after a page reload.
  const backgroundRenderIds = useMemo(() => {
    const ids = new Set(renderingIds);
    sessions.forEach(s => { if (s.is_rendering) ids.add(s.session_id); });
    if (renderSession?.session_id) ids.delete(renderSession.session_id);
    return [...ids].sort();
  }, [renderingIds, sessions, renderSession]);
  const backgroundRenderKey = backgroundRenderIds.join(',');

  useEffect(() => {
    if (!backgroundRenderKey) return undefined;

    const sessionIds = backgroundRenderKey.split(',');
    let disposed = false;
    let timer = null;

    const poll = async () => {
      const results = await Promise.allSettled(
        sessionIds.map(async sessionId => ({ sessionId, status: await getRenderStatus(sessionId) }))
      );
      if (disposed) return;

      const terminalIds = results
        .filter(result => result.status === 'fulfilled' && TERMINAL_RENDER_STATUSES.has(result.value.status.status))
        .map(result => result.value.sessionId);

      if (terminalIds.length > 0) {
        const terminalSet = new Set(terminalIds);
        setRenderingIds(prev => {
          const next = new Set(prev);
          terminalSet.forEach(id => next.delete(id));
          return next;
        });
        // getRenderStatus persists terminal state; refresh both view modes from
        // the session index so is_rendering/render_failed/video flags agree.
        refreshSessions().catch(() => {});
      }

      // Keep retrying while this effect remains active. A successful refresh
      // removes completed IDs from backgroundRenderKey and cleans this timer up.
      timer = setTimeout(poll, 3000);
    };

    poll();
    return () => {
      disposed = true;
      if (timer) clearTimeout(timer);
    };
  }, [backgroundRenderKey, refreshSessions]);

  // Admin: recompute a session onto the correct track, then refresh the list
  // so its canonical_track_name updates. The track catalog itself (reassignTracks)
  // doesn't change from this — no need to refetch it.
  const handleReassignTrack = useCallback(async (sessionId, trackId) => {
    const res = await reassignSessionTrack(sessionId, trackId);
    refreshSessions().catch(() => {});
    return res;
  }, [refreshSessions]);

  const refreshStats = useCallback(() => {
    getSessionStats().then(setStats).catch(() => {});
  }, []);

  const loadMoreSessions = useCallback(() => {
    if (!hasMore || loadingMore || !cursor) return;
    setLoadingMore(true);
    listSessions({ ...buildBaseParams(), cursor })
      .then(data => {
        setSessions(prev => [...prev, ...data.sessions]);
        setCursor(data.next_cursor);
        setHasMore(!!data.next_cursor);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  }, [hasMore, loadingMore, cursor, buildBaseParams]);

  // Infinite scroll: fetch the next page once the sentinel at the bottom of the
  // list nears the viewport.
  const scrollSentinelRef = useRef(null);
  useEffect(() => {
    const el = scrollSentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMoreSessions(); },
      { rootMargin: '600px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMoreSessions]);

  // Career summary + per-track best lap — fetched once, independent of the
  // Recent Sessions filters (the backend always scopes it to the user's own
  // non-shared-to-them sessions).
  useEffect(() => { refreshStats(); }, [refreshStats]);

  // Re-fetch when an upload succeeds (incl. force-past-duplicate). The app-level
  // upload paths open the new session in the analysis view, so this guarantees
  // the list reflects it without depending on remount timing. Skip the initial
  // mount (signal 0) since the effect above already loads the list.
  useEffect(() => {
    if (!uploadSignal) return;
    refreshSessions().catch(() => {});
    refreshStats();
  }, [uploadSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Batch queue helpers ───────────────────────────────────────────────────
  const updateQueueItem = (id, updates) => {
    queueRef.current = queueRef.current.map(i => i.id === id ? { ...i, ...updates } : i);
    setQueueDisplay([...queueRef.current]);
  };

  const processQueue = async () => {
    if (processingRef.current) return;
    processingRef.current = true;

    // One worker loop: grabs the next queued item and uploads it, then repeats.
    // The find+updateQueueItem pair is synchronous (no await between them), so
    // multiple workers can run concurrently without claiming the same item.
    const runWorker = async () => {
      while (true) {
        const next = queueRef.current.find(i => i.status === 'queued');
        if (!next) break;

        if (!next.kind) {
          updateQueueItem(next.id, { status: 'error', errorMsg: 'Unsupported file type' });
          continue;
        }

        // Claim this item before any await so other workers skip it
        updateQueueItem(next.id, { status: 'uploading', progress: 0 });
        try {
          const apiFn = next.kind === 'video'      ? uploadVideo
                      : next.kind === 'smartycam3' ? uploadSmartycam3
                      : next.kind === 'xrk'        ? uploadXrk
                      : next.kind === 'motec'      ? uploadMotec
                      : uploadCsv;
          const opts = {};
          if (next.kind === 'motec' && next.sidecar) opts.sidecar = next.sidecar;
          if (next.recipientEmail) opts.recipientEmail = next.recipientEmail;
          await apiFn(next.file, pct => {
            if (pct < 100) updateQueueItem(next.id, { progress: pct });
            else updateQueueItem(next.id, { status: 'processing' });
          }, opts);
          updateQueueItem(next.id, { status: 'done', progress: 100 });
        } catch (err) {
          if (err.isDuplicate) {
            updateQueueItem(next.id, {
              status: 'skipped',
              skippedAs: { id: err.existingSessionId, name: err.existingSessionName },
            });
          } else {
            updateQueueItem(next.id, { status: 'error', errorMsg: err.message });
          }
        }
      }
    };

    try {
      // Run BATCH_CONCURRENCY workers in parallel; each drains from the shared queue
      await Promise.all(Array.from({ length: BATCH_CONCURRENCY }, runWorker));
    } finally {
      processingRef.current = false;
      // Refresh session list after batch completes
      try { await refreshSessions(); } catch { /* best-effort */ }
      refreshStats();
    }
  };

  const enqueueFiles = (files) => {
    const arr = Array.from(files);
    // An all-XRK batch (e.g. a folder scan) gets a higher cap than mixed uploads.
    const cap = arr.length > 0 && arr.every(isXrkFile) ? XRK_BATCH_MAX : BATCH_MAX;
    const accepted = arr.slice(0, cap);
    if (arr.length > cap) {
      setBatchWarning(`Only the first ${cap} files were queued (${arr.length} dropped).`);
      setTimeout(() => setBatchWarning(''), 5000);
    } else {
      setBatchWarning('');
    }
    // Pair MoTeC .ldx sidecars onto their matching .ld by shared basename, and
    // drop standalone .ldx files (they carry no telemetry and aren't uploaded alone).
    const ldxByStem = new Map();
    for (const f of accepted) {
      const n = f.name.toLowerCase();
      if (n.endsWith('.ldx')) ldxByStem.set(n.slice(0, -4), f);
    }
    const items = accepted
      .filter(f => !f.name.toLowerCase().endsWith('.ldx'))
      .map(f => {
        const kind = classifyFile(f);
        const stem = f.name.toLowerCase().replace(/\.[^.]+$/, '');
        return {
          id: crypto.randomUUID(),
          file: f,
          kind,
          sidecar: kind === 'motec' ? (ldxByStem.get(stem) || null) : null,
          recipientEmail: (currentUser?.is_admin && adminModeActive && adminRecipientEmail) ? adminRecipientEmail : null,
          status: 'queued',
          progress: 0,
          errorMsg: null,
          skippedAs: null,
        };
      });
    queueRef.current = [...queueRef.current, ...items];
    setQueueDisplay([...queueRef.current]);
    processQueue();
  };

  const clearQueue = () => {
    queueRef.current = [];
    setQueueDisplay([]);
  };

  // ── Group helpers ─────────────────────────────────────────────────────────
  const toggleCollapse = (key) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      localStorage.setItem('ta_collapsedGroups', JSON.stringify([...next]));
      return next;
    });
  };

  const renameGroup = (key, label) => {
    setGroupLabels(prev => {
      const next = { ...prev, [key]: label };
      localStorage.setItem('ta_groupLabels', JSON.stringify(next));
      return next;
    });
  };

  // Everything an EventGroupHeader needs beyond the group itself, in one place
  // so the card and table views can't drift apart on when sharing or deleting a
  // whole event is offered (never for events containing someone else's session).
  const groupHeaderProps = (group) => {
    const allOwned = group.sessions.every(s => !s.is_shared);
    const selectableIds = groupSelectableIds(group);
    const selectedCount = selectableIds.filter(id => selectedIds.has(id)).length;
    const selectState = selectedCount === 0 ? 'none'
      : selectedCount === selectableIds.length ? 'all' : 'some';
    return {
      isGroupShared: group.sessions.every(s => sharedByMeIds.has(s.session_id)),
      selectState,
      // Standard tri-state: fully selected → clear the event, otherwise select
      // every member (including a partial selection's remainder). The shift-
      // click anchor is dropped so a later range can't start from a stale row.
      onToggleSelect: selectableIds.length > 0 ? () => {
        setSelectedIds(prev => {
          const next = new Set(prev);
          if (selectState === 'all') selectableIds.forEach(id => next.delete(id));
          else selectableIds.forEach(id => next.add(id));
          return next;
        });
        lastClickedIdRef.current = null;
      } : undefined,
      onToggle:      () => toggleCollapse(group.key),
      onRename:      (label) => renameGroup(group.key, label),
      onShareGroup:  sessionSharing && allOwned ? () => setSharingGroup(group) : undefined,
      onDeleteGroup: allOwned ? () => setConfirmDeleteGroup(group) : undefined,
    };
  };

  // Clicking the toggle while grouping is suspended means "I want my groups
  // back", which is only possible in date order — so restore the sort too.
  const toggleTableGrouping = () => {
    if (tableGrouped && sort.col !== 'date') {
      setSort({ col: 'date', dir: 'desc' });
      return;
    }
    const next = !tableGrouped;
    setTableGrouped(next);
    localStorage.setItem('ta_tableGrouped', next ? '1' : '0');
  };

  // Shared by event-group delete and multi-select bulk delete — both just
  // hand this a list of session objects to remove.
  const handleConfirmDelete = async (sessionsToDelete) => {
    const ids = sessionsToDelete.map(s => s.session_id);
    let result;
    try {
      result = await bulkDeleteSessions(ids);
    } catch (err) {
      alert(`Failed to delete sessions: ${err.message}`);
      setConfirmDeleteGroup(null);
      setConfirmBulkDelete(null);
      return;
    }
    const deletedIds = new Set(result.deleted);
    setSessions(prev => prev.filter(s => !deletedIds.has(s.session_id)));
    setSelectedIds(prev => { const n = new Set(prev); deletedIds.forEach(id => n.delete(id)); return n; });
    // Clean up local overrides for deleted sessions
    setLocalSharedIds(prev => { const n = new Set(prev); deletedIds.forEach(id => n.delete(id)); return n; });
    setStandaloneIds(prev => {
      const n = new Set(prev);
      deletedIds.forEach(id => n.delete(id));
      localStorage.setItem('ta_standaloneIds', JSON.stringify([...n]));
      return n;
    });
    setConfirmDeleteGroup(null);
    setConfirmBulkDelete(null);
    refreshStats();
    if (result.skipped?.length > 0) {
      alert(`${result.skipped.length} session${result.skipped.length !== 1 ? 's' : ''} could not be deleted (not found, or not owned by you).`);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (ids) => {
    setSelectedIds(prev => {
      const allSelected = ids.length > 0 && ids.every(id => prev.has(id));
      return allSelected ? new Set() : new Set(ids);
    });
  };

  // Plain click toggles a single row and re-anchors; shift-click unions the
  // contiguous visual range (within visibleIds) into the existing selection.
  const handleSelectClick = (id, visibleIds, shiftKey) => {
    const anchorIdx = shiftKey ? visibleIds.indexOf(lastClickedIdRef.current) : -1;
    const curIdx = visibleIds.indexOf(id);

    if (shiftKey && anchorIdx !== -1 && curIdx !== -1) {
      const [lo, hi] = anchorIdx < curIdx ? [anchorIdx, curIdx] : [curIdx, anchorIdx];
      const rangeIds = visibleIds.slice(lo, hi + 1);
      setSelectedIds(prev => {
        const next = new Set(prev);
        rangeIds.forEach(rid => next.add(rid));
        return next;
      });
      return;
    }

    toggleSelect(id);
    lastClickedIdRef.current = id;
  };

  const removeFromGroup = (sessionId) => {
    setStandaloneIds(prev => {
      const next = new Set([...prev, sessionId]);
      localStorage.setItem('ta_standaloneIds', JSON.stringify([...next]));
      return next;
    });
  };

  // ── Single-file handlers (existing flow with duplicate dialog) ────────────
  const handleFile = (file) => {
    // An ECU log isn't a session — it attaches to one. Send it to the matcher,
    // which works out which session that is.
    if (isEcuOnlyFile(file)) { setEcuMatchFiles([file]); return; }
    // A video belongs to a session that is usually already uploaded (the
    // logger's own file arrives first). The matcher finds which; a SmartyCam3
    // file that matches nothing can still create a session from there.
    if (isVideoFile(file) && !(currentUser?.is_admin && adminModeActive && adminRecipientEmail)) {
      setVideoMatchFiles([file]); return;
    }
    if (isXrzFile(file)) { alert(XRZ_REJECT_MSG); return; }
    const kind = classifyFile(file);
    if (!kind) {
      alert('Unsupported file type. Please drop an AiM RS3 CSV, a RaceChrono Pro CSV (v3), a Harry\'s LapTimer VBO, a Garmin Catalyst CSV, an AiM XRK binary, a MoTeC .ld (with optional .ldx), or a SmartyCam3 .mp4.');
      return;
    }
    // In admin mode with a recipient email, always use the batch path so the
    // email is attached to the upload (the prop chain doesn't carry it).
    if (currentUser?.is_admin && adminModeActive && adminRecipientEmail) {
      enqueueFiles([file]);
      return;
    }
    setUploadFilename(file.name);
    if (kind === 'video' && onUploadVideo) onUploadVideo(file);
    else if (kind === 'smartycam3' && onUploadSmartycam3) onUploadSmartycam3(file);
    else if (kind === 'xrk' && onUploadXrk) onUploadXrk(file);
    else if (kind === 'motec' && onUploadMotec) onUploadMotec(file);
    else onUpload(file);
  };

  // Recursively scan dropped folder entries for XRK files, then enqueue them.
  const scanFoldersForXrk = async (entries) => {
    setScanningFolder(true);
    setBatchWarning('');
    const out = [];
    const skipped = { xrz: 0 };
    try {
      await Promise.all(entries.map(en => collectXrkFromEntry(en, out, skipped)));
    } finally {
      setScanningFolder(false);
    }
    if (out.length === 0) {
      setBatchWarning(noXrkFoundMessage('dropped', skipped.xrz));
      setTimeout(() => setBatchWarning(''), 5000);
      return;
    }
    setPendingBatch(out);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const dt = e.dataTransfer;
    // Capture FileSystem entries synchronously — the DataTransfer is cleared once
    // the drop handler returns, so any async folder walk must start from entries
    // grabbed here (not from dt.items later).
    const entries = dt.items
      ? Array.from(dt.items).map(i => i.webkitGetAsEntry?.()).filter(Boolean)
      : [];
    if (entries.some(en => en.isDirectory)) {
      scanFoldersForXrk(entries);
      return;
    }
    const files = dt.files;
    if (!files || files.length === 0) return;
    routeDroppedFiles(Array.from(files));
  };

  // A drop of ECU logs goes to the matcher; anything else follows the normal
  // single/batch upload paths. A mixed drop is treated as a session upload —
  // the batch confirm modal is where the user sorts that out.
  const routeDroppedFiles = (arr) => {
    if (arr.length && arr.every(isEcuOnlyFile)) { setEcuMatchFiles(arr); return; }
    if (arr.length && arr.every(isVideoFile)
        && !(currentUser?.is_admin && adminModeActive && adminRecipientEmail)) {
      setVideoMatchFiles(arr); return;
    }
    if (arr.length === 1) handleFile(arr[0]);
    else setPendingBatch(arr);
  };

  const handleDownload = async (e, sessionId, type, filename) => {
    e.stopPropagation();
    setDownloadingId(`${sessionId}:${type}`);
    try {
      await downloadSessionFile(sessionId, type, filename);
    } catch (err) {
      alert(`Download failed: ${err.message}`);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (e, sessionId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this session? This cannot be undone.')) return;
    setDeletingId(sessionId);
    try {
      await deleteSession(sessionId);
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      refreshStats();
    } catch (err) {
      alert(`Failed to delete session: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const startEdit = (e, s) => {
    e.stopPropagation();
    setEditingId(s.session_id);
    setEditDraft({
      session_label: s.session_label || '',
      vehicle:       s.vehicle === 'Unknown' ? '' : (s.vehicle || ''),
      racer:         s.racer   === 'Unknown' ? '' : (s.racer   || ''),
    });
  };

  const saveEdit = async (e, sessionId) => {
    e.stopPropagation();
    setSavingEdit(true);
    try {
      const updated = await patchSessionMeta(sessionId, editDraft);
      setSessions(prev => prev.map(s =>
        s.session_id === sessionId
          ? { ...s, session_label: updated.session_label || '', vehicle: updated.vehicle || 'Unknown', racer: updated.racer || 'Unknown' }
          : s
      ));
      setEditingId(null);
      refreshStats();
    } catch (err) {
      alert(`Failed to update session: ${err.message}`);
    } finally {
      setSavingEdit(false);
    }
  };

  const cancelEdit = (e) => { e.stopPropagation(); setEditingId(null); };

  // Track-record badges — per-track best lap from /sessions/stats (career-wide,
  // not just the currently loaded page(s)).
  const trackBests = stats.track_bests;

  const isTrackRecord = (s) => {
    const track = trackKey(s);
    return track && s.best_lap_seconds != null &&
      trackBests[track] != null && s.best_lap_seconds <= trackBests[track] + 0.001;
  };

  const loadingMsg = loading === 'video' ? 'Extracting telemetry from video…'
    : loading === 'smartycam3' ? 'Extracting telemetry from SmartyCam3 video…'
    : loading === 'xrk' ? 'Parsing XRK file…'
    : 'Parsing session data…';

  // ── Session card renderer (shared by group and flat views) ────────────────
  const renderCard = (s, { isInGroup = false, isGroupBest = false, onRemoveFromGroup } = {}) => (
    <div key={s.session_id} className={styles.cardWrapper}>
      <div
        className={`${styles.sessionCard} ${!s.can_reload ? styles.cardDisabled : ''} ${isGroupBest ? styles.cardGroupBest : ''}`}
        onClick={() => s.can_reload && !loading && deletingId !== s.session_id && handleLoadSession(s.session_id, s.can_comment !== false, s.is_owner !== false)}
        role="button"
        tabIndex={s.can_reload && !loading && deletingId !== s.session_id ? 0 : -1}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); s.can_reload && !loading && deletingId !== s.session_id && handleLoadSession(s.session_id, s.can_comment !== false, s.is_owner !== false); } }}
        aria-disabled={!!loading || !s.can_reload || deletingId === s.session_id}
        title={!s.can_reload ? 'Original CSV not available — re-upload to restore' : undefined}
      >
        <div className={styles.cardTop}>
          <input
            type="checkbox"
            className={styles.cardSelectBox}
            checked={selectedIds.has(s.session_id)}
            onClick={e => { e.stopPropagation(); shiftKeyRef.current = e.shiftKey; }}
            onChange={() => handleSelectClick(s.session_id, cardVisibleIds, shiftKeyRef.current)}
            aria-label={`Select session ${trackDisplayName(s) || ''}`}
          />
          <span className={styles.cardTrack}>{trackDisplayName(s) || 'Session'}</span>
          <div className={styles.cardTopRight}>
            {desktopSync && <SyncStatusBadge item={syncItemsBySession.get(s.session_id)} />}
            {isTrackRecord(s) && <span className={styles.trackRecordBadge} title="Fastest lap at this track">⚑ Track Best</span>}
            {s.has_video && <span className={styles.videoTag}><Film size={12} /></span>}
            {s.has_rendered_video && <span className={styles.videoTag} title="Overlay video ready"><Film size={12} />✓</span>}
            <HealthChip status={s.health_status} findings={s.health_findings} unchecked={s.health_unchecked} />
            {sessionSharing && (s.is_shared
              ? <span className={styles.sharedBadge} title="Shared with you">↗ Shared</span>
              : sharedByMeIds.has(s.session_id)
              ? <button className={styles.sharedByMeBadge} onClick={e => { e.stopPropagation(); setSharingSession(s); }} title="Shared — click to manage" aria-label="Manage sharing">↗ Shared</button>
              : editingId !== s.session_id && (
                <button className={styles.shareBtn} onClick={e => { e.stopPropagation(); setSharingSession(s); }} title="Share session" aria-label="Share session">↗ Share</button>
              )
            )}
            {!s.is_shared && editingId !== s.session_id && (
              <button className={styles.editBtn} onClick={e => startEdit(e, s)} title="Edit session info" aria-label="Edit session info">✎</button>
            )}
            {!s.is_shared && editingId !== s.session_id && currentUser?.is_admin && adminModeActive && (
              <button
                className={styles.shareBtn}
                onClick={e => { e.stopPropagation(); setSendLinkSession(s); }}
                title="Send share link by email"
                aria-label="Send link"
              >✉ Send Link</button>
            )}
            {!s.is_shared && editingId !== s.session_id && currentUser?.is_admin && adminModeActive && (
              <button
                className={styles.shareBtn}
                onClick={e => { e.stopPropagation(); setReassignSession(s); }}
                title="Reassign track (admin)"
                aria-label="Reassign track"
              >↪ Fix Track</button>
            )}
            {!s.is_shared && editingId !== s.session_id && (
              s.render_failed ? (
                <button className={styles.renderFailedBadge} title="Render failed — click for details / retry" onClick={e => { e.stopPropagation(); openRenderModal(s, 3); }}>⚠ Render failed</button>
              ) : (s.is_rendering || renderingIds.has(s.session_id)) ? (
                <button className={styles.renderingBadge} title="Render in progress — click to view status" onClick={e => { e.stopPropagation(); openRenderModal(s, 3); }}>⏳ Rendering…</button>
              ) : (
                <button
                  className={styles.addVideoBtn}
                  onClick={e => { e.stopPropagation(); openRenderModal(s, 1); }}
                  title={(s.has_rendered_video || s.has_gopro_files) ? 'Re-render overlay video' : 'Add overlay video'}
                  aria-label="Add overlay video"
                >
                  <Film size={12} /> {(s.has_rendered_video || s.has_gopro_files) ? 'Re-render' : 'Add Video'}
                </button>
              )
            )}
            {!s.is_shared && editingId !== s.session_id && (
              <button
                className={styles.deleteBtn}
                onClick={e => handleDelete(e, s.session_id)}
                disabled={deletingId === s.session_id}
                title="Delete session"
                aria-label="Delete session"
              >
                {deletingId === s.session_id ? '…' : '×'}
              </button>
            )}
          </div>
        </div>

        {editingId === s.session_id ? (
          <div className={styles.editForm} onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
            <div className={styles.editRow}>
              <label className={styles.editLabel}>Session</label>
              <input className={styles.editInput} value={editDraft.session_label} placeholder="e.g. Novice 1" onChange={e => setEditDraft(d => ({ ...d, session_label: e.target.value }))} autoFocus />
            </div>
            <div className={styles.editRow}>
              <label className={styles.editLabel}>Car</label>
              <input className={styles.editInput} value={editDraft.vehicle} placeholder="Vehicle" onChange={e => setEditDraft(d => ({ ...d, vehicle: e.target.value }))} />
            </div>
            <div className={styles.editRow}>
              <label className={styles.editLabel}>Driver</label>
              <input className={styles.editInput} value={editDraft.racer} placeholder="Driver name" onChange={e => setEditDraft(d => ({ ...d, racer: e.target.value }))} />
            </div>
            <div className={styles.editActions}>
              <button className={styles.editSaveBtn} onClick={e => saveEdit(e, s.session_id)} disabled={savingEdit}>{savingEdit ? '…' : 'Save'}</button>
              <button className={styles.editCancelBtn} onClick={cancelEdit}>Cancel</button>
            </div>
          </div>
        ) : (
          <>
            {s.session_label && <div className={styles.cardLabel}>{s.session_label}</div>}
            <div className={styles.cardMeta}>
              {s.vehicle && <span className={styles.metaItem}><Car size={12} /> {s.vehicle}</span>}
              {s.racer   && <span className={styles.metaItem}><User size={12} /> {s.racer}</span>}
            </div>

            <div className={styles.cardStats}>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Best Lap</span>
                <span className={styles.statValue}>{s.best_lap_time || '—'}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Laps</span>
                <span className={styles.statValue}>{s.lap_count ?? '—'}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Duration</span>
                <span className={styles.statValue}>{fmtDuration(s.duration)}</span>
              </div>
            </div>

            {s.weather && (() => {
              const w = convertWeatherUnits(s.weather, unitPrefs || { temp: 'F', speed: 'mph', pressure: 'psi' });
              return (
                <div className={styles.cardWeather}>
                  <span className={styles.weatherIcon}>{weatherIcon(s.weather.condition)}</span>
                  <span className={styles.weatherTemp}>{w.temp ?? '—'}</span>
                  <span className={styles.weatherCond}>{w.condition}</span>
                  {s.weather.precipitation_mm > 0 && <span className={styles.weatherPrecip}>{w.precip}</span>}
                </div>
              );
            })()}

            <div className={styles.cardDate}>
              {s.date && <span>{s.date}{s.time ? ` ${fmtTime12(s.time)}` : ''}</span>}
              {s.created_at && <span className={styles.dateUploaded}>Uploaded {fmtDateTime(s.created_at)}</span>}
            </div>

            {(s.has_original || s.can_reload || s.has_video || s.has_original_video || s.has_rendered_video) && (
              <div className={styles.cardDownloads} onClick={e => e.stopPropagation()}>
                {s.has_original && (
                  <button className={styles.downloadBtn} onClick={e => handleDownload(e, s.session_id, 'original', s.original_filename)} disabled={!!downloadingId} title={`Download original file${s.original_filename ? ` (${s.original_filename})` : ''}`}>
                    {downloadingId === `${s.session_id}:original` ? '…' : '↓ Original'}
                  </button>
                )}
                {s.can_reload && (
                  <button className={styles.downloadBtn} onClick={e => handleDownload(e, s.session_id, 'csv')} disabled={!!downloadingId} title="Download processed telemetry CSV">
                    {downloadingId === `${s.session_id}:csv` ? '…' : '↓ CSV'}
                  </button>
                )}
                {s.has_video && (
                  <button className={styles.downloadBtn} onClick={e => handleDownload(e, s.session_id, 'video')} disabled={!!downloadingId} title="Download converted MP4 video">
                    {downloadingId === `${s.session_id}:video` ? '…' : '↓ Video (MP4)'}
                  </button>
                )}
                {s.has_original_video && (
                  <button className={styles.downloadBtn} onClick={e => handleDownload(e, s.session_id, 'video_original')} disabled={!!downloadingId} title="Download original MOV file">
                    {downloadingId === `${s.session_id}:video_original` ? '…' : '↓ Video (MOV)'}
                  </button>
                )}
                {s.has_rendered_video && (
                  <a
                    href={`/api/rendered_video/${s.session_id}?download=1`}
                    className={styles.downloadBtn}
                    title="Download overlay video"
                    onClick={e => e.stopPropagation()}
                    style={{ textDecoration: 'none' }}
                  >↓ Video</a>
                )}
              </div>
            )}

            {isInGroup && !s.is_shared && onRemoveFromGroup && (
              <button
                className={styles.ungroupBtn}
                onClick={e => { e.stopPropagation(); onRemoveFromGroup(); }}
                title="Remove from this event group"
              >
                ⊘ Remove from event
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.panel}>
      <CircuitBg />

      <div className={styles.content}>
        <p className={styles.sub}>Drop an AiM RS3 CSV or XRK export, a RaceChrono Pro CSV (v3), a Harry's LapTimer VBO file, a Garmin Catalyst CSV, a MoTeC .ld log (with optional .ldx), or a SmartyCam3 .mp4 (telemetry is extracted automatically). Video footage from a GoPro, dashcam, phone or SmartyCam can be dropped here too — it is matched to the session it was recorded in.</p>

        <div
          className={`${styles.dropzone} ${dragging ? styles.dragging : ''}`}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
        >
          {loading && uploadProgress !== null ? (
            <UploadProgressBar progress={uploadProgress} filename={uploadFilename} />
          ) : loading === 'video' ? (
            <VideoConvertingLoader />
          ) : loading ? (
            <LightsOutLoader message={loadingMsg} />
          ) : scanningFolder ? (
            <LightsOutLoader message="Scanning folder for XRK files…" />
          ) : (
            <>
              <SpeedTraceIcon />
              <p className={styles.dropLabel}>
                Drop files here, or <span className={styles.link}>click to browse</span>
              </p>
              <p className={styles.dropHint}>Up to {BATCH_MAX} files at once</p>
              <p className={styles.dropHint}>
                Or{' '}
                <span
                  className={styles.link}
                  onClick={e => { e.stopPropagation(); folderInputRef.current?.click(); }}
                >drop / select a folder</span>
                {' '}to scan it (and its subfolders) for XRK files (up to {XRK_BATCH_MAX})
              </p>
              <div className={styles.formats}>
                <span className={styles.formatItem}>
                  <span className={styles.badge}>CSV</span>
                  <span className={styles.badgeSub}>AiM RS3 · RaceChrono v3 · Garmin Catalyst</span>
                </span>
                <span className={styles.formatItem}>
                  <span className={styles.badge}>VBO</span>
                  <span className={styles.badgeSub}>Harry's LapTimer</span>
                </span>
                <span className={styles.formatItem}>
                  <span className={styles.badge}>XRK</span>
                  <span className={styles.badgeSub}>AiM binary</span>
                </span>
                <span className={styles.formatItem}>
                  <span className={styles.badge}>LD</span>
                  <span className={styles.badgeSub}>MoTeC i2</span>
                </span>
              </div>
            </>
          )}
        </div>

        {/* Engine-computer logs attach to an already-uploaded session rather
            than creating one, so they get their own entrance. */}
        <p className={styles.ecuEntry}>
          Have logs from your engine computer?{' '}
          <button
            type="button"
            className={styles.ecuEntryLink}
            onClick={() => setEcuMatchFiles([])}
          >
            Match ECU logs to your sessions
          </button>
        </p>
        <p className={styles.ecuEntry}>
          Camera footage from a track day?{' '}
          <button
            type="button"
            className={styles.ecuEntryLink}
            onClick={() => setVideoMatchFiles([])}
          >
            Match videos to your sessions
          </button>
        </p>

        {/* Hidden file inputs live OUTSIDE the dropzone: a programmatic .click() on
            an input dispatches a click that bubbles up the DOM, and if the input
            were a child of the dropzone that bubble would hit the dropzone's onClick
            and re-open the plain file picker on top of the folder picker. */}
        <input
          ref={inputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={e => {
            const files = e.target.files;
            if (!files || files.length === 0) return;
            routeDroppedFiles(Array.from(files));
            e.target.value = '';
          }}
        />
        <input
          ref={folderInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={e => {
            const all = Array.from(e.target.files || []);
            const xrk = all.filter(isXrkFile);
            e.target.value = '';
            if (xrk.length === 0) {
              setBatchWarning(noXrkFoundMessage('selected', all.filter(isXrzFile).length));
              setTimeout(() => setBatchWarning(''), 5000);
              return;
            }
            setPendingBatch(xrk);
          }}
        />

        {currentUser?.is_admin && adminModeActive && (
          <div className={styles.adminRecipientRow}>
            <label className={styles.adminRecipientLabel}>Upload on behalf of</label>
            <input
              className={styles.adminRecipientInput}
              type="email"
              placeholder="recipient@example.com (optional)"
              value={adminRecipientEmail}
              onChange={e => setAdminRecipientEmail(e.target.value)}
            />
            {adminRecipientEmail && (
              <button className={styles.filterClear} onClick={() => setAdminRecipientEmail('')} title="Clear">×</button>
            )}
          </div>
        )}

        {error && <div className={styles.error}>⚠ {error}</div>}
        {batchWarning && <div className={styles.batchWarning}>⚠ {batchWarning}</div>}
        {multiSessionNotice && (
          <div className={styles.batchWarning} onClick={onClearMultiSessionNotice} style={{ cursor: 'pointer' }}>
            ℹ {multiSessionNotice}
          </div>
        )}

        {/* Batch queue */}
        <BatchQueuePanel queue={queueDisplay} onClear={clearQueue} />

        {pendingBatch && (
          <UploadConfirmModal
            files={pendingBatch}
            onCancel={() => setPendingBatch(null)}
            onConfirm={(sel) => { setPendingBatch(null); enqueueFiles(sel); }}
          />
        )}

        {syncDialog && (
          <SyncPlanDialog
            scopeLabel={syncDialog.label}
            plan={syncPlan}
            loading={syncPlanning}
            error={syncError}
            onClose={() => { setSyncDialog(null); setSyncPlan(null); setSyncError(''); }}
            onStart={startPlannedSync}
            onConnect={() => {
              setSyncDialog(null);
              setSyncPlan(null);
              onOpenServerSync?.();
            }}
          />
        )}

        {videoMatchFiles && (
          <VideoMatchModal
            initialFiles={videoMatchFiles}
            onClose={() => setVideoMatchFiles(null)}
            onAttached={() => { refreshSessions().catch(() => {}); }}
            onSessionCreated={() => { refreshSessions().catch(() => {}); refreshStats(); }}
            // The files are already in the session's video dir, so the render
            // wizard opens at the overlay step.
            onRender={(sid) => { setVideoMatchFiles(null); openRenderModal({ session_id: sid }, 2); }}
          />
        )}

        {ecuMatchFiles && (
          <EcuMatchModal
            initialFiles={ecuMatchFiles}
            onClose={() => setEcuMatchFiles(null)}
            // A merge changes a session's channels and sets its ECU badge, so
            // the list has to be re-read.
            onMerged={() => { refreshSessions().catch(() => {}); }}
          />
        )}

        {/* VTR render queue */}
        <VtrRenderQueue />

        {/* Feature discovery. Most of what it advertises lives behind the View
            menu of a loaded session, which is exactly why it needs announcing
            here, on the page every driver lands on. */}
        <WhatsNew />

        {/* Recent sessions */}
        {!loadingSessions && hasAnySessions && (
          <div className={styles.recentSection}>
            <div className={styles.recentHeader}>
              <h3 className={styles.recentTitle}>Recent Sessions</h3>
              <SessionPrimaryFilters
                filterText={filterText} onFilterText={setFilterText} facets={facets}
                filterTrack={filterTrack} onFilterTrack={setFilterTrack}
                filterCar={filterCar} onFilterCar={setFilterCar}
                filterDriver={filterDriver} onFilterDriver={setFilterDriver}
              />
              <div className={styles.viewControls}>
                {desktopSync && (
                  <button
                    className={styles.syncAllBtn}
                    onClick={() => openSyncPlan(null, 'Sync all local sessions')}
                  >
                    <CloudUpload size={13} /> Sync all
                  </button>
                )}
                {viewMode === 'table' && (
                  <button
                    className={`${styles.groupToggleBtn} `
                      + (tableGroupingActive ? styles.groupToggleBtnActive
                         : tableGrouped ? styles.groupToggleBtnPaused : '')}
                    onClick={toggleTableGrouping}
                    title={tableGrouped && !tableGroupingActive
                      ? `Grouping paused — sorted by ${TABLE_COLS.find(c => c.key === sort.col)?.label || sort.col}. Click to sort by date and group again.`
                      : 'Group sessions into events (same track, within 3 days)'}
                  >⁝⁝ Events</button>
                )}
                <div className={styles.viewToggle}>
                  <button
                    className={`${styles.viewToggleBtn} ${viewMode === 'cards' ? styles.viewToggleBtnActive : ''}`}
                    onClick={() => switchView('cards')}
                    title="Card view"
                  >⊞ Cards</button>
                  <button
                    className={`${styles.viewToggleBtn} ${viewMode === 'table' ? styles.viewToggleBtnActive : ''}`}
                    onClick={() => switchView('table')}
                    title="Table view"
                  >≡ Table</button>
                </div>
              </div>
            </div>

            {desktopSync && (
              <SyncQueueSummary
                queue={syncQueue}
                onPause={() => updateSyncQueue(pauseDesktopSyncQueue)}
                onResume={() => updateSyncQueue(resumeDesktopSyncQueue)}
                onRetry={() => updateSyncQueue(retryDesktopSyncQueue)}
              />
            )}
            {desktopSync && (syncError || syncPollError) && !syncDialog && (
              <div className={styles.syncInlineError}>⚠ {syncError || syncPollError}</div>
            )}

            <SessionAdvancedFilters
              tempBounds={tempBounds} tempRange={tempRange} onTempRange={setTempRange} tempUnit={tempUnit}
              conditionAvailability={condAvail} conditions={conditions} onConditions={setConditions}
              dateBounds={dateBounds} dateRange={dateRange} onDateRange={setDateRange}
              dateField={dateField} onDateField={setDateField}
              dateSliderMin={dateSliderMin} dateSliderMax={dateSliderMax}
              activeDatePreset={activeDatePreset} onDatePreset={applyDatePreset}
              formatDate={fmtDate}
            />

            {stats.sessions > 0 && (
              <div className={styles.statsSummary}>
                You've analyzed <strong>{stats.laps.toLocaleString()}</strong> lap{stats.laps !== 1 ? 's' : ''}
                {' '}across <strong>{stats.sessions.toLocaleString()}</strong> session{stats.sessions !== 1 ? 's' : ''}
                {' '}on <strong>{stats.tracks.toLocaleString()}</strong> track{stats.tracks !== 1 ? 's' : ''}
                {' · '}<strong>{Math.round(stats.hours).toLocaleString()}</strong> hour{Math.round(stats.hours) !== 1 ? 's' : ''} of track time
              </div>
            )}

            {sessionSharing && (
              <SessionOwnershipTabs activeTab={activeTab} onActiveTab={setActiveTab} />
            )}

            {selectedIds.size > 0 && (() => {
              // Shared sessions can be reported on (and synced) but never
              // deleted — the backend would skip them as forbidden, so don't
              // offer it. Admins acting as admins own everything here.
              const ownedSelected = sessions.filter(s => selectedIds.has(s.session_id) && (!s.is_shared || isAdmin));
              const sharedCount = selectedIds.size - ownedSelected.length;
              return (
              <div className={styles.bulkToolbar}>
                <span className={styles.bulkToolbarText}>
                  {selectedIds.size} session{selectedIds.size === 1 ? '' : 's'}
                  {selectedEventCount > 0 && ` · in ${selectedEventCount} event${selectedEventCount === 1 ? '' : 's'}`}
                </span>
                <button className={styles.bulkClearBtn} onClick={() => setSelectedIds(new Set())}>Clear</button>
                {onOpenReport && (
                  <button
                    className={styles.bulkReportBtn}
                    onClick={() => {
                      const params = { session_ids: [...selectedIds], title: selectionEventTitle };
                      // A report is usually one event; spanning several is
                      // legal but almost always a stray tick, so ask first.
                      if (selectedEventCount > 1) setConfirmMultiEventReport(params);
                      else onOpenReport(params);
                    }}
                    title="Build a report from the selected sessions"
                  >
                    <FileText size={13} /> Report
                  </button>
                )}
                {desktopSync && (
                  <button
                    className={styles.bulkSyncBtn}
                    onClick={() => openSyncPlan(
                      [...selectedIds],
                      `Sync ${selectedIds.size} selected session${selectedIds.size === 1 ? '' : 's'}`,
                    )}
                  >
                    <CloudUpload size={13} /> Sync selected
                  </button>
                )}
                {ownedSelected.length > 0 && (
                  <button
                    className={styles.bulkDeleteBtn}
                    onClick={() => setConfirmBulkDelete(ownedSelected)}
                    title={sharedCount > 0
                      ? `Only your own sessions can be deleted — ${sharedCount} shared session${sharedCount === 1 ? '' : 's'} will be left alone`
                      : undefined}
                  >
                    {sharedCount > 0 ? `Delete ${ownedSelected.length} owned` : 'Delete selected'}
                  </button>
                )}
              </div>
              );
            })()}

            {(() => {
              // Filtering/sorting is server-side now — effectiveSessions is
              // already the correctly filtered, correctly sorted set for the
              // page(s) loaded so far. "+" signals more pages exist server-side
              // beyond what's been loaded (via infinite scroll).
              //
              // This line is also the only place a stale filter announces
              // itself: filters persist across visits, so a user who narrowed
              // the list last week and forgot sees an inexplicably short (or
              // empty) list today. Say what's happening and offer the way out.
              //
              // A paused event grouping gets the same treatment: the Events
              // toggle's dashed state is easy to miss, and without this line the
              // only explanation is in its tooltip.
              const groupingPaused = viewMode === 'table' && tableGrouped && !tableGroupingActive;
              const sortLabel = TABLE_COLS.find(c => c.key === sort.col)?.label || sort.col;
              const countLine = (filtersActive || groupingPaused) && (
                <div className={`${styles.filterCount} ${filtersActive && effectiveSessions.length === 0 ? styles.filterCountEmpty : ''}`}>
                  {!filtersActive
                    ? `Sorted by ${sortLabel} — sessions group into events in date order`
                    : effectiveSessions.length === 0
                      ? 'No sessions match the current filters'
                      : `${effectiveSessions.length}${hasMore ? '+' : ''} session${effectiveSessions.length !== 1 ? 's' : ''} match the current filters`}
                  <button type="button" className={styles.filterReset} onClick={clearAllFilters}>
                    {filtersActive ? 'Clear all filters' : 'Sort by date'}
                  </button>
                </div>
              );

              if (viewMode === 'table') {
                return (
                  <>
                    {countLine}
                    <SessionTable
                      sessions={effectiveSessions}
                      sort={sort}
                      onSort={handleSort}
                      trackBests={trackBests}
                      unitPrefs={unitPrefs}
                      onLoadSession={handleLoadSession}
                      loading={loading}
                      deletingId={deletingId}
                      onIndexVideo={handleIndexVideo}
                      indexingId={indexingId}
                      sharedByMeIds={sharedByMeIds}
                      renderingIds={renderingIds}
                      onShare={sessionSharing ? setSharingSession : null}
                      onDelete={handleDelete}
                      onRender={(s) => openRenderModal(s, 1)}
                      onRenderStatus={(s) => openRenderModal(s, 3)}
                      onReassign={setReassignSession}
                      isAdmin={isAdmin}
                      selectedIds={selectedIds}
                      onSelectClick={handleSelectClick}
                      onToggleSelectAll={toggleSelectAll}
                      grouped={tableGroupingActive}
                      groups={tableGroups}
                      collapsedGroups={collapsedGroups}
                      groupHeaderProps={groupHeaderProps}
                      desktopSync={desktopSync}
                      syncItemsBySession={syncItemsBySession}
                    />
                  </>
                );
              }

              return (
                <>
                  {countLine}
                  <div className={styles.groupedList}>
                    {groups.map(group => {
                      // Sessions with no recognizable track/date — render as individual cards
                      if (group.isUngrouped) {
                        return group.sessions.map(s => renderCard(s, {}));
                      }

                      const isCollapsed    = collapsedGroups.has(group.key);
                      const stats          = groupStats(group);
                      const isSolo         = group.sessions.length === 1;
                      const groupBestId    = isSolo ? null : group.sessions.reduce((best, s) => {
                        if (s.best_lap_seconds == null) return best;
                        if (best == null || s.best_lap_seconds < best.secs) return { id: s.session_id, secs: s.best_lap_seconds };
                        return best;
                      }, null)?.id;

                      return (
                        <div key={group.key} className={styles.eventGroup}>
                          <EventGroupHeader
                            group={group}
                            collapsed={isCollapsed}
                            stats={stats}
                            {...groupHeaderProps(group)}
                          />
                          {!isCollapsed && (
                            <>
                              {!isSolo && (
                                <div className={styles.groupStatsBar}>
                                  {stats.bestLap && (
                                    <span className={styles.groupStatItem}>
                                      Best Lap <strong className={styles.groupStatValue}>{stats.bestLap.time}</strong>
                                    </span>
                                  )}
                                  {stats.totalLaps > 0 && (
                                    <span className={styles.groupStatItem}>
                                      <strong className={styles.groupStatValue}>{stats.totalLaps}</strong> laps
                                    </span>
                                  )}
                                  {stats.totalDuration > 0 && (
                                    <span className={styles.groupStatItem}>
                                      <strong className={styles.groupStatValue}>{fmtDuration(stats.totalDuration)}</strong> total
                                    </span>
                                  )}
                                </div>
                              )}
                              <div className={styles.sessionGrid}>
                                {group.sessions.map(s =>
                                  renderCard(s, {
                                    isInGroup: true,
                                    isGroupBest: s.session_id === groupBestId,
                                    onRemoveFromGroup: isSolo ? undefined : () => removeFromGroup(s.session_id),
                                  })
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
            <div ref={scrollSentinelRef} className={styles.scrollSentinel} aria-hidden="true" />
            {loadingMore && <div className={styles.filterCount}>Loading more…</div>}
          </div>
        )}
      </div>

      {renderSession && (
        <VideoRenderModal
          sessionId={renderSession.session_id}
          isAdmin={isAdmin}
          availableChannels={(renderSession.channels || []).map((name, i) => ({ name, unit: (renderSession.units || [])[i] || '' }))}
          unitPrefs={unitPrefs}
          hasExistingFiles={!!(renderSession.has_rendered_video || renderSession.has_gopro_files)}
          sessionDuration={renderSession.meta?.duration ?? renderSession.duration ?? 0}
          lastRenderOverlays={renderSession.last_render_overlays ?? null}
          initialStep={renderInitialStep}
          onClose={() => { setRenderSession(null); setRenderInitialStep(1); }}
          onRenderStarted={() => {
            const sid = renderSession.session_id;
            setRenderingIds(prev => new Set([...prev, sid]));
          }}
          onRenderComplete={() => {
            const sid = renderSession.session_id;
            setRenderingIds(prev => { const n = new Set(prev); n.delete(sid); return n; });
            setRenderSession(null);
            refreshSessions().catch(() => {});
          }}
          onRenderCanceled={() => {
            const sid = renderSession.session_id;
            setRenderingIds(prev => { const n = new Set(prev); n.delete(sid); return n; });
            setRenderSession(null);
            setRenderInitialStep(1);
            refreshSessions().catch(() => {});
          }}
        />
      )}

      {sessionSharing && sharingSession && (
        <ShareModal
          sessionId={sharingSession.session_id}
          sessionName={sharingSession.session_name}
          onClose={() => setSharingSession(null)}
          onShared={() => {
            setLocalSharedIds(prev => new Set([...prev, sharingSession.session_id]));
            refreshSessions().catch(() => {});
          }}
          onUnshared={() => {
            const sid = sharingSession.session_id;
            refreshSessions().then(data => {
              const still = data.sessions.find(s => s.session_id === sid);
              if (!still?.has_shares) {
                setLocalSharedIds(prev => { const n = new Set(prev); n.delete(sid); return n; });
              }
            }).catch(() => {});
          }}
        />
      )}

      {duplicateInfo && (
        <DuplicateDialog
          info={duplicateInfo}
          onOpenExisting={() => { onClearDuplicate?.(); handleLoadSession(duplicateInfo.existingSessionId); }}
          onUploadAnyway={() => onForceUpload?.()}
          onDismiss={() => onClearDuplicate?.()}
        />
      )}

      {confirmDeleteGroup && (
        <DeleteSessionsDialog
          title="Delete event"
          subtitle={confirmDeleteGroup.label || getGroupDefaultName(confirmDeleteGroup)}
          sessions={confirmDeleteGroup.sessions}
          onConfirm={handleConfirmDelete}
          onDismiss={() => setConfirmDeleteGroup(null)}
        />
      )}

      {confirmBulkDelete && (
        <DeleteSessionsDialog
          title="Delete selected sessions"
          sessions={confirmBulkDelete}
          onConfirm={handleConfirmDelete}
          onDismiss={() => setConfirmBulkDelete(null)}
        />
      )}

      {confirmMultiEventReport && (
        <MultiEventReportDialog
          events={selectedEvents}
          sessionCount={confirmMultiEventReport.session_ids.length}
          onConfirm={() => { const p = confirmMultiEventReport; setConfirmMultiEventReport(null); onOpenReport(p); }}
          onDismiss={() => setConfirmMultiEventReport(null)}
        />
      )}

      {sessionSharing && sharingGroup && (
        <GroupShareModal
          sessions={sharingGroup.sessions}
          onClose={() => setSharingGroup(null)}
          onAllShared={() => {
            setLocalSharedIds(prev => new Set([...prev, ...sharingGroup.sessions.map(s => s.session_id)]));
            refreshSessions().catch(() => {});
            setSharingGroup(null);
          }}
          onAllUnshared={() => {
            const ids = sharingGroup.sessions.map(s => s.session_id);
            refreshSessions().then(() => {
              setLocalSharedIds(prev => {
                const n = new Set(prev);
                ids.forEach(id => n.delete(id));
                return n;
              });
            }).catch(() => {});
          }}
        />
      )}

      {sendLinkSession && (
        <AdminSendLinkModal
          session={sendLinkSession}
          adminDisplayName={currentUser?.display_name || currentUser?.email}
          onClose={() => setSendLinkSession(null)}
          onSent={() => {
            setSendLinkSession(null);
            refreshSessions().catch(() => {});
          }}
        />
      )}

      {reassignSession && (
        <ReassignTrackModal
          sessionId={reassignSession.session_id}
          driver={reassignSession.racer || 'Driver'}
          lapTimeStr={reassignSession.best_lap_time}
          vehicleName={reassignSession.vehicle}
          eventDate={reassignSession.date}
          currentTrackName={reassignSession.canonical_track_name}
          tracks={reassignTracks}
          onReassign={handleReassignTrack}
          onClose={() => setReassignSession(null)}
        />
      )}
    </div>
  );
}
